
import React, { useEffect, useRef, useState } from 'react';

import localforage from 'localforage';
import { kmeans } from 'ml-kmeans';
import dbscan from '@cdxoo/dbscan'
import {
  EMA,
  SMMA,
  Extremums,
  Pivot,
  ATR,
  PSAR,
  AO,
  AC,
  Stochastic,
  StochasticRSI,
  RSI,
  CCI,
  ROC,
  DC,
  cRSI,
  ADX,
  ChaikinOscillator,
} from '@debut/indicators';

import {
  KeyLevelsFinder,
  KeyLevelFilter,
  WaveFinder,
  WaveStatsManager,
  WaveStatsManagerWorker,
  ExtremumStatsManager,
  // levels
  findKeyLevels,
  filterKeyLevels,
  // waves
  getWaves,
  createWaveHierarchy,
  compressWaveCascades,
  // wave stats
  calculateStatistics,
  getSimpleWaveStats,
  getWaveClusterStats,
  analyzeOutcomesByCluster,
  getSpecificClusterStats,
  getWaveExtremStats,
  getBounceSL,
  getBounceEnter,
  findOptimalWaveWindow,
  getWaveLevels,
  getSequenceStats,
  predictSequence,
  getWaveContinuation,
} from './CandleUtils';

import {
  indicatorsList,
  BacktestSettings,
  strategiesConfig,
} from '#state/settings';
import {
  getCandleBacktest,
  setCandleBacktest,
} from '#state/trades';
import {
  calculateFadeExpectancy,
  getBlendedExpectancy,
  calculateClusterFadeExpectancy,
  getClustersPaths,
  getGroupedOutcomes,
} from '#state/utils';

import { PortfolioManager, Position } from './BacktestUtils';

// *************************************************
// view config
// *************************************************

const STATS_MAX_WAVES = 200
const STATS_START_INCREMENT = 100

// levels
const ATR_MAIN_LENGTH = 14
const ATR_TEMP_LENGTH = 14
// waves

// backtest
const marketFee = 0.0005
const limitFee = 0.0002
const cashbackRate = 0.35
// 
const zeroProfitPercent = 0.001
const initialBalance = 100
const positionPercent = 1
const pyramiding = 999
const minTargetPercent = 0.004
// 
const bouncePreLvlEnterPoints = 3
const bouncePostLvlEnterPoints = 3
// 
const longIgnoreEnabled = true
const shortIgnoreEnabled = true

const logEnters = false
const logExits = false

const waveWindows = [15] // [3, 5, 15, 30, 45, 60]


export async function waveBacktest(props) {
  console.log('waveBacktest STARTED')

  const {
    currentChart, currentSymbol, symbolInfo,
    tempLevelSeries, candles: allCandles,
    intervalMinutes, f1Percent, backtestSettings,
    tweakpane,
  } = props;

  const monitor = {
    progress: 0,
  }
  const twpProgress = tweakpane.addBinding(monitor, 'progress', {
    label: 'Backtest Progress',
    readonly: true,
  });

  try {
    const portfolioManager = new PortfolioManager(initialBalance, { market: marketFee, limit: limitFee }, cashbackRate);
    const candleTimeMap = new Map();
    let lastCandleIndexInMap = -1;
    if (lastCandleIndexInMap < allCandles.length - 1) {
      for (let i = lastCandleIndexInMap + 1; i < allCandles.length; i++) {
        candleTimeMap.set(allCandles[i].time, i);
      }
      lastCandleIndexInMap = allCandles.length - 1;
    }

    const atrMain = new ATR(ATR_MAIN_LENGTH);
    const atrMainPre = new ATR(ATR_MAIN_LENGTH);
    const atrMainPreCandles = allCandles.slice(0, ATR_MAIN_LENGTH + 1)
    let atrMainPreValue = 0
    for (let i = 0; i < atrMainPreCandles.length; i++) {
      const candle = atrMainPreCandles[i]
      atrMainPreValue = atrMainPre.nextValue(candle.open, candle.close, candle.high, candle.low)
    }

    let indicators = {}
    initIndicators(indicators, allCandles)
    calculateIndicators(indicators, atrMainPreCandles)

    // 
    let skippedCandlesLong = 0
    let skippedCandlesShort = 0

    let state = getDefaultState()
    let stateInverse = getDefaultState()

    const expectancyDebug = []
    const expectancyDebugPortfolio = []
    let calcCache = []
    let simpleStatsCache
    let wavesChanges = []
    let wavesNew = []
    let wavesLastWaveUpdate = []
    let wavesExteremUpdate = []
    let shared = {
      skippedCandlesLong,
      skippedCandlesShort,
    }

    const storage = {
      waveStatsStartIndex: 0,
      // 
      waveVarians: null,
      optimalWaves: null,
      optimalStats: null,
      prevLastWave: { start: 0, end: 0 },
      prevWaveUpdated: { start: 0, end: 0 },
      lastWaveUpdated: { start: 0, end: 0 },
      shared,
      // 
      selectedWindow: waveWindows[0],
      simpleStatsCache,
    }

    const predictionDetails = {
      totalPredictions: 0,
      correctPredictions: 0,
      totalMove: 0,
      totalPullback: 0,
    }

    const levelsFinder = new KeyLevelsFinder(intervalMinutes, atrMain, atrMainPreValue);
    const initialCandles = allCandles.slice(0, ATR_MAIN_LENGTH)
    levelsFinder.update(initialCandles);

    const levelFilters = {}
    const waveFinders = {}
    const statsManagers = {}
    const extremumStatsManagers = {}
    for (const window of waveWindows) {
      const levelFilter = new KeyLevelFilter(window, f1Percent);
      levelFilters[window] = levelFilter

      const finder = new WaveFinder();
      waveFinders[window] = finder

      const statsManager = new WaveStatsManager({
        windowSize: 100,
      });
      // statsManager.reset()
      statsManagers[window] = statsManager

      const extremumManager = new ExtremumStatsManager();
      extremumStatsManagers[window] = extremumManager
    }

    const perfResults = {}


    // **********************************************
    // HANDLE CANDLES
    // **********************************************

    for (let i = ATR_MAIN_LENGTH; i < allCandles.length; i++) {
      let opts = {
        i, allCandles,
        candleTimeMap,
        indicators,
        // const
        backtestSettings,
        symbolInfo,
        levelsFinder,
        levelFilters,
        waveFinders,
        statsManagers,
        extremumStatsManagers,
        // 
        expectancyDebug,
        expectancyDebugPortfolio,
        wavesChanges,
        wavesNew,
        wavesLastWaveUpdate,
        wavesExteremUpdate,
        calcCache,
        state,
        // other
        predictionDetails,
        perfResults,
        monitor,
        storage,
        portfolioManager,
      }
      await onNewCandle(opts)
    }

    console.log('BACKTEST RESULTS ***********************************')
    const results = portfolioManager.getStats();
    console.log('BACKTEST RESULTS portfolioManager:', results, portfolioManager);

    console.log('BACKTEST optimalWaves', storage.optimalWaves)
    console.log('BACKTEST optimalStats', storage.optimalStats)

    console.log('BACKTEST perfResults', perfResults)
    // for (const f in perfResults) {
    //   console.log(`--- Функция ${f} ---`);
    //   console.log(`  Среднее время выполнения: ${(perfResults[f].total / perfResults[f].count).toFixed(4)} мс`);
    //   console.log(`  Суммарное время выполнения: ${perfResults[f].total.toFixed(4)} мс`);
    // }


    console.log('wave type predictions details', predictionDetails)

    console.log('STATE', state)
    console.log('STATE INVERSE', stateInverse)



    // console.log('calcCache ***********************************', calcCache)
    console.log('expectancyDebug', expectancyDebug, expectancyDebugPortfolio)

    // const {
    //   buySeriesLong,
    //   sellSeriesLong,
    //   buySeriesShort,
    //   sellSeriesShort,
    //   buySeriesDataLong,
    //   sellSeriesDataLong,
    //   buySeriesDataShort,
    //   sellSeriesDataShort,
    // } = backtestTradesSeries

    // console.log('backtestTradesSeries', backtestTradesSeries)

    // buySeriesDataLong.sort((a, b) => a.time - b.time)
    // sellSeriesDataLong.sort((a, b) => a.time - b.time)
    // buySeriesDataShort.sort((a, b) => a.time - b.time)
    // sellSeriesDataShort.sort((a, b) => a.time - b.time)

    // try {
    //   buySeriesLong.setData(buySeriesDataLong);
    // } catch (error) {
    //   console.log('buySeriesLong.setData(buySeriesDataLong); error', error)
    // }
    // try {
    //   sellSeriesLong.setData(sellSeriesDataLong);
    // } catch (error) {
    //   console.log('sellSeriesLong.setData(sellSeriesDataLong); error', error)
    // }
    // try {
    //   buySeriesShort.setData(buySeriesDataShort);
    // } catch (error) {
    //   console.log('buySeriesShort.setData(buySeriesDataShort); error', error)
    // }
    // try {
    //   sellSeriesShort.setData(sellSeriesDataShort);
    // } catch (error) {
    //   console.log('sellSeriesShort.setData(sellSeriesDataShort); error', error)
    // }

    twpProgress.dispose();

    return {
      portfolioManager,
      expectancyDebug,
      expectancyDebugPortfolio,
      wavesChanges,
      // wavesNew,
      // wavesLastWaveUpdate,
      // wavesExteremUpdate,
      allCandles,
      state,
      stateInverse
    }
  } catch (error) {
    console.log('waveBacktest error', error)
  }
  twpProgress.dispose();

}

setCandleBacktest(waveBacktest)


async function onNewCandle(props) {

  const {
    i, allCandles,
    candleTimeMap,
    indicators,
    // const
    backtestSettings,
    symbolInfo,
    levelsFinder,
    levelFilters,
    waveFinders,
    statsManagers,
    extremumStatsManagers,
    // 
    expectancyDebug,
    expectancyDebugPortfolio,
    wavesChanges,
    wavesNew,
    wavesLastWaveUpdate,
    wavesExteremUpdate,
    calcCache,
    state,
    // other
    predictionDetails,
    perfResults,
    monitor,
    storage,
    portfolioManager,
  } = props

  // const {
  //   waveVarians,
  //   optimalWaves,
  //   optimalStats,
  //   prevLastWave,
  //   prevWaveUpdated,
  //   shared,
  //   // 
  //   lastWaveUpdated,
  //   selectedWindow,
  //   simpleStatsCache,
  //   waveStatsStartIndex,
  // } = storage

  if (!portfolioManager || typeof portfolioManager.balance !== 'number') {
    console.error('ОШИБКА в onNewCandle: portfolioManager или его баланс некорректны!', portfolioManager);
    return; // Прерываем выполнение, если что-то не так
  }

  if (i % 10 === 0) {
    await sleep(0);
  }
  if (i % 200 === 0) {
    console.log('Backtest', i, 'of', allCandles.length)
  }

  const candlesEnter = allCandles.slice(0, i)
  const candles = allCandles.slice(0, i - 1)
  const lastCandle = candles[candles.length - 1]
  const prevCandle = candles[candles.length - 2]

  let hasNewWave = false
  let isLastWaveEndTimeUpdated = false
  let isExtremsUpdated = false

  const startIndicators = performance.now();
  calculateIndicators(indicators, [lastCandle])
  updatePerf(startIndicators, 'calculateIndicators', perfResults)

  // *************************************************
  // LEVELS & WAVES 
  // *************************************************

  const startLvlWaves = performance.now();
  const startFindKeyLevels = performance.now();
  // const foundLevels = findKeyLevels(candles, intervalMinutes, atrMain, atrMainPreValue)
  const foundLevels = levelsFinder.update([lastCandle]);
  updatePerf(startFindKeyLevels, 'findKeyLevels', perfResults)

  const timeToCheckWindow = i === ATR_MAIN_LENGTH || i % 100 === 0
  if (timeToCheckWindow) {
    const waveWindowsInitOptions = []
    for (const window of waveWindows) {
      // const keyLevels = filterKeyLevels(foundLevels, window, f1Percent)
      // const { highLevels, lowLevels } = keyLevels
      // const wavesData = getWaves(lowLevels, highLevels)

      const startFilterKeyLevels = performance.now();
      const levelFilter = levelFilters[window]
      const keyLevels = levelFilter.update(foundLevels);
      const { filteredHighs, filteredLows } = keyLevels
      updatePerf(startFilterKeyLevels, 'filterKeyLevels', perfResults)

      const startGetWaves = performance.now();
      const waveFinder = waveFinders[window]
      const wavesData = waveFinder.update(filteredLows, filteredHighs, candles, candleTimeMap);
      updatePerf(startGetWaves, 'getWaves', perfResults)

      const startStats = performance.now();
      // const stats = getSimpleWaveStats(wavesData.waves)
      const { waves } = wavesData
      const statsManager = statsManagers[window]
      // const newWaves = waves.slice(statsManager.totalWaves, -1); // Получаем только новые волны
      // if (newWaves.length > 0) {
      //   await statsManager.addWaves(newWaves);
      // }
      if (statsManager.processedWaves.length >= STATS_MAX_WAVES) {
        storage.waveStatsStartIndex += STATS_START_INCREMENT
        statsManager.reset()
        console.log('statsManager.reset()')
      }
      statsManager.update(waves.slice(storage.waveStatsStartIndex));

      const stats = await statsManager.getStats();
      storage.simpleStatsCache = stats

      updatePerf(startStats, 'getSimpleWaveStats', perfResults)

      const duration = stats.pullback.duration.median
      const delta = stats.pullback.deltaPercent.median
      waveWindowsInitOptions.push({
        window,
        duration,
        delta,
        keyLevels,
        wavesData,
        stats,
      })
    }
    const { volatility, duration, minPercent } = BacktestSettings.waveWindowOptimizer
    const optimalWaveWindows = findOptimalWaveWindow(waveWindowsInitOptions, volatility, duration, minPercent)
    const optimal = optimalWaveWindows.optimal
    storage.waveVarians = optimalWaveWindows

    if (optimal.window !== storage.selectedWindow) {
      console.log('SET NEW WAVE WINDOW', optimal.window, storage.selectedWindow)
    }

    storage.selectedWindow = optimal.window
    storage.optimalWaves = optimal.wavesData.waves
    storage.optimalStats = optimal.stats
  } else {
    // const keyLevels = filterKeyLevels(foundLevels, selectedWindow, f1Percent)
    // const { highLevels, lowLevels } = keyLevels
    // const wavesData = getWaves(lowLevels, highLevels)

    const startFilterKeyLevels = performance.now();
    const levelFilter = levelFilters[storage.selectedWindow]
    const keyLevels = levelFilter.update(foundLevels);
    const { filteredHighs, filteredLows } = keyLevels
    updatePerf(startFilterKeyLevels, 'filterKeyLevels', perfResults)

    const startGetWaves = performance.now();
    // const wavesData = getWaves(filteredLows, filteredHighs)
    const waveFinder = waveFinders[storage.selectedWindow]
    const wavesData = waveFinder.update(filteredLows, filteredHighs, candles, candleTimeMap);
    updatePerf(startGetWaves, 'getWaves', perfResults)

    const startStats = performance.now();
    // const stats = getSimpleWaveStats(wavesData.waves)
    const { waves } = wavesData
    const statsManager = statsManagers[storage.selectedWindow]
    // const newWaves = waves.slice(statsManager.totalWaves -1); // Получаем только новые волны
    // if (newWaves.length > 0) {
    //   await statsManager.addWaves(newWaves);
    // }
    if (statsManager.processedWaves.length >= STATS_MAX_WAVES) {
      storage.waveStatsStartIndex += STATS_START_INCREMENT
      statsManager.reset()
      console.log('statsManager.reset()')
    }

    let stats
    if (!storage.simpleStatsCache || waves.length % 3 === 1) {
      statsManager.update(waves.slice(storage.waveStatsStartIndex));
      stats = await statsManager.getStats();
      storage.simpleStatsCache = stats
    } else {
      stats = storage.simpleStatsCache
    }

    updatePerf(startStats, 'getSimpleWaveStats', perfResults)

    storage.optimalWaves = wavesData.waves
    storage.optimalStats = stats
  }

  updatePerf(startLvlWaves, 'lvlWaves', perfResults)

  if (storage.optimalWaves.length < 20) {
    return
  }

  const currentWave = storage.optimalWaves[storage.optimalWaves.length - 1]

  if (storage.prevLastWave.start === currentWave.start && storage.prevLastWave.direction === currentWave.direction) {
    if (storage.prevLastWave.end === currentWave.end) {
      // no new wave, no update
    } else if (storage.prevLastWave.delta !== currentWave.delta) {
      isExtremsUpdated = true
    } else {
      // end time updated
      isLastWaveEndTimeUpdated = true
    }
  } else {
    hasNewWave = true
  }

  storage.prevLastWave = storage.optimalWaves[storage.optimalWaves.length - 1]

  const optimalSubset = storage.optimalWaves.slice(-4)
  const noLastSubset = storage.optimalWaves.slice(-4, -1)


  const lastWaveUpdated = storage.lastWaveUpdated
  const waveContinuation = getWaveContinuation(storage.optimalWaves, storage.optimalStats, candles, storage.prevWaveUpdated)
  if (waveContinuation.isNewWave || waveContinuation.waveUpdated) {
    storage.lastWaveUpdated = Object.assign({}, currentWave)
    lastWaveUpdated.start = waveContinuation.start
    lastWaveUpdated.end = waveContinuation.end
    lastWaveUpdated.high = waveContinuation.high
    lastWaveUpdated.low = waveContinuation.low
    lastWaveUpdated.delta = waveContinuation.delta
    lastWaveUpdated.deltaPercent = waveContinuation.deltaPercent
    lastWaveUpdated.type = waveContinuation.type ? waveContinuation.type : currentWave.type
    lastWaveUpdated.direction = waveContinuation.direction ? waveContinuation.direction : currentWave.direction

    if (waveContinuation.isNewWave) {
      optimalSubset.push({ ...lastWaveUpdated })
      storage.optimalWaves.push({ ...lastWaveUpdated, isContinuation: true, undetected: !hasNewWave })
      hasNewWave = true
    } else if (waveContinuation.waveUpdated) {
      optimalSubset.splice(-1, 1, { ...lastWaveUpdated })
      storage.optimalWaves.splice(-1, 1, { ...lastWaveUpdated, isExtremsUpdated: true, undetected: !isExtremsUpdated })
      isExtremsUpdated = true
    }

    // storage.shared.closestHighsPricesRev = storage.shared.closestLowsPrices.map(level => level + waveTargetDeltaDown)
    // storage.shared.closestLowsPricesRev = storage.shared.closestHighsPrices.map(level => level - waveTargetDeltaUp)

  }

  storage.prevWaveUpdated = storage.lastWaveUpdated
  // console.log('waveContinuation iteration', i, 'of', allCandles.length, waveContinuation)

  // ***********************************************
  // OTHER
  // ***********************************************

  const hasChanges = hasNewWave || isExtremsUpdated

  if (hasNewWave) {
    const up = {
      ...storage.optimalWaves[storage.optimalWaves.length - 1],
      selectedWindow: storage.selectedWindow,
      ...storage.shared,
      detectTime: lastCandle.time,
      newWave: true,
    }
    wavesChanges.push(up)
    wavesNew.push(up)
  } else if (isLastWaveEndTimeUpdated) {
    const up = {
      ...storage.optimalWaves[storage.optimalWaves.length - 1],
      selectedWindow: storage.selectedWindow,
      ...storage.shared,
      detectTime: lastCandle.time,
      lastUp: true,
    }
    // wavesChanges.push(up)
    wavesLastWaveUpdate.push(up)
  } else if (isExtremsUpdated) {
    const up = {
      ...storage.lastWaveUpdated,
      selectedWindow: storage.selectedWindow,
      ...storage.shared,
      detectTime: lastCandle.time,
      extremUp: true,
    }
    wavesChanges.push(up)
    wavesExteremUpdate.push(up)
  }

  // ***********************************************
  // CALCULATIONS
  // ***********************************************

  if (hasChanges) {
    const lastRealWave = storage.optimalWaves[storage.optimalWaves.length - 2]



    const startCalculatePrices = performance.now();

    const c = calculatePrices({
      selectedWindow: storage.selectedWindow,
      optimalWaves: storage.optimalWaves,
      optimalStats: storage.optimalStats,
      optimalSubset,
      noLastSubset,
      lastWaveUpdated,
      waveContinuation,
      prevLastWave: storage.prevLastWave,
      extremumManager: extremumStatsManagers[storage.selectedWindow],
      calcCache,
    })

    updatePerf(startCalculatePrices, 'calculatePrices', perfResults)

    const {
      calc,
      predictedCurrentWaveData,
    } = c

    storage.shared = {
      optimalStats: storage.optimalStats,
      optimalWaves: storage.optimalWaves,
      waveVarians: storage.waveVarians,
      lastCandle,
      prevCandle,
      lastRealWave,
      prevLastWave: storage.prevLastWave,
      lastWaveUpdated,
      currentWave,
      waveContinuation,
      calc
    }

    // Wave Type Prediction Stats 
    if (hasNewWave) {
      wavePredictionsStats({
        optimalSubset,
        predictedCurrentWaveData,
        predictionDetails,
      })
    }

  }

  // ***********************************************
  // TRADES
  // ***********************************************

  const startTrades = performance.now();

  checkExitLong(candlesEnter, storage.shared, state, indicators, hasChanges, symbolInfo, backtestSettings)
  checkExitShort(candlesEnter, storage.shared, state, indicators, hasChanges, symbolInfo, backtestSettings)

  checkEnterLong(false, candlesEnter, storage.shared, state, indicators, hasChanges, symbolInfo, backtestSettings, expectancyDebug)
  checkEnterShort(false, candlesEnter, storage.shared, state, indicators, hasChanges, symbolInfo, backtestSettings, expectancyDebug)

  if (hasChanges) {
    checkEnterLongManager(false, candlesEnter, storage.shared, state, indicators, hasChanges, symbolInfo, backtestSettings, expectancyDebugPortfolio, portfolioManager)
    checkEnterShortManager(false, candlesEnter, storage.shared, state, indicators, hasChanges, symbolInfo, backtestSettings, expectancyDebugPortfolio, portfolioManager)
  }
  // final updates
  // prevLastWave = optimalWaves[optimalWaves.length - 1]

  updatePerf(startTrades, 'trades', perfResults)

  const currentWaveCount = storage.optimalWaves.length;
  portfolioManager.update(lastCandle, currentWaveCount);

  monitor.progress = (i / allCandles.length * 100).toFixed(2)
}



export function calculatePrices(props) {
  const {
    selectedWindow,
    optimalWaves,
    optimalStats,
    optimalSubset,
    noLastSubset,
    lastWaveUpdated,
    waveContinuation,
    prevLastWave,
    extremumManager,
    calcCache,
  } = props

  const isNewWave = waveContinuation.isNewWave
  const realShift = isNewWave ? 2 : 1
  const lastRealWave = optimalWaves[optimalWaves.length - realShift]

  const t = {
    ctx: {},
    stats: {},
  }

  const waveLevels = getWaveLevels(optimalWaves)
  t.ctx.waveLevels = waveLevels
  const highLevels = waveLevels?.highs || []
  const lowLevels = waveLevels?.lows || []
  // const closestHighs = highLevels
  // const closestHighsFiltered = highLevels.filter(level => {
  //   return true // level.start + minLevelTime < lastCandle.time
  // })
  t.closestHighs = highLevels //closestHighsFiltered//.slice(-1)
  t.closestHighsPrices = t.closestHighs.map(level => level.high)

  // const closestLows = lowLevels
  // const closestLowsFiltered = lowLevels.filter(level => {
  //   return true // level.start + minLevelTime < lastCandle.time
  // })
  t.closestLows = lowLevels //closestLowsFiltered //.slice(-1)
  t.closestLowsPrices = t.closestLows.map(level => level.low)


  // const wavesFourPoints0 = fourPoints(optimalWaves, 0)
  // t.ctx.wavesFourPoints0 = wavesFourPoints0
  // const wavesFourPoints1 = fourPoints(optimalWaves, 1)
  // t.ctx.wavesFourPoints1 = wavesFourPoints1


  // const waveHierarchy = createWaveHierarchy(optimalWaves, 'W');
  // t.ctx.waveHierarchy = waveHierarchy
  // const cascadeStages = compressWaveCascades(waveHierarchy)
  // t.ctx.cascadeStages = cascadeStages

  // if (cascadeStages.length) {
  //   const lastStage = cascadeStages[cascadeStages.length - 1]
  //   const waveCascadesHierarchy = lastStage.hierarchy
  //   t.ctx.waveCascadesHierarchy = waveCascadesHierarchy
  // }

  // const hWaves = t.ctx.waveCascadesHierarchy || t.ctx.waveHierarchy
  // const hierarchyFourPoints0 = fourPoints(hWaves, 0)
  // t.ctx.hierarchyFourPoints0 = hierarchyFourPoints0
  // const hierarchyFourPoints1 = fourPoints(hWaves, 1)
  // t.ctx.hierarchyFourPoints1 = hierarchyFourPoints1

  // const waveExtremStats = getWaveExtremStats(optimalWaves)
  const waveExtremStats = extremumManager.get(optimalWaves);

  t.stats.waveExtremStats = waveExtremStats

  const bounceEnterLong = getBounceEnter(waveExtremStats, 'up', 5, 50)
  t.bounceEnterLong = bounceEnterLong
  const bounceEnterShort = getBounceEnter(waveExtremStats, 'down', 5, 50)
  t.bounceEnterShort = bounceEnterShort

  const bounceSLPercentLong = getBounceSL(waveExtremStats, 'down', 5, 8) // test rev directionup
  t.bounceSLPercentLong = bounceSLPercentLong
  const bounceSLPercentShort = getBounceSL(waveExtremStats, 'up', 5, 8)
  t.bounceSLPercentShort = bounceSLPercentShort

  const dp = optimalStats?.pullback?.deltaPercent?.ema || optimalStats?.pullback?.deltaPercent
  const avg1 = dp?.avg
  const median1 = dp?.median
  // const stdDev1 = dp?.stdDev
  // if (avg1 - stdDev1 < minimalVolatilityFilter) return null
  const waveTargetPercent = Math.min(avg1, median1)
  t.waveTargetPercent = waveTargetPercent

  const sequenceStats = getSequenceStats(optimalWaves)
  t.stats.sequenceStats = sequenceStats
  const predictedSequence = predictSequence(optimalSubset, sequenceStats);
  t.ctx.predictionsWave = predictedSequence
  t.predictedWave = predictedSequence?.[1]?.prediction[0]
  const ratioTypeNext = t.predictedWave ? t.predictedWave.type : 'pullback'

  const predictedCurrentWave = predictSequence(noLastSubset, sequenceStats);
  t.ctx.predictionsCurrentWave = predictedCurrentWave
  t.predictedCurrentWave = predictedCurrentWave?.[1]?.prediction[0]
  const predictedCurrentWaveData = predictedCurrentWave?.[1]?.prediction[0]
  const ratioTypeCurrent = t.predictedCurrentWave ? t.predictedCurrentWave.type : 'pullback'


  // *************************************
  // Strategy Expectancies
  // *************************************
  const currentExpLong = getExpectancy({
    side: 'long',
    lastWave: optimalWaves[optimalWaves.length - 1],
    prevWave: optimalWaves[optimalWaves.length - 2],
    optimalStats,
  })
  const currentExpShort = getExpectancy({
    side: 'short',
    lastWave: optimalWaves[optimalWaves.length - 1],
    prevWave: optimalWaves[optimalWaves.length - 2],
    optimalStats,
  })
  const prevExpLong = getExpectancy({
    side: 'long',
    lastWave: optimalWaves[optimalWaves.length - 2],
    prevWave: optimalWaves[optimalWaves.length - 3],
    optimalStats,
  })
  const prevExpShort = getExpectancy({
    side: 'short',
    lastWave: optimalWaves[optimalWaves.length - 2],
    prevWave: optimalWaves[optimalWaves.length - 3],
    optimalStats,
  })

  const expectancies = {
    currentLong: currentExpLong,
    currentShort: currentExpShort,
    prevLong: prevExpLong,
    prevShort: prevExpShort,
  }
  t.expectancies = expectancies

  // *************************************
  // Cluster Expectancies
  // *************************************
  let waveClusterStatsPercent
  let waveClusterStatsRatio

  if (!calcCache[optimalWaves.length]) {
    const optimalWavesSubset = optimalWaves.slice(0, -1)
    // const waveRatioStats = getWaveClusterStats(optimalWaves)
    waveClusterStatsPercent = getWaveClusterStats(optimalWavesSubset)
    waveClusterStatsRatio = getWaveClusterStats(optimalWavesSubset, 'deltaPercent', 'ratio')
    t.stats.waveClusterStatsPercent = waveClusterStatsPercent
    t.stats.waveClusterStatsRatio = waveClusterStatsRatio
    t.stats.waveClusterStats = {
      deltaPercent: waveClusterStatsPercent,
      deltaRatioPrev: waveClusterStatsRatio,
    }
    const outcomesByClusterPercent = analyzeOutcomesByCluster(optimalWavesSubset, waveClusterStatsPercent)
    const outcomesByClusterRatio = analyzeOutcomesByCluster(optimalWavesSubset, waveClusterStatsRatio, 'deltaPercent', 'ratio')
    t.stats.waveClusterOutcomes = {
      deltaPercent: outcomesByClusterPercent,
      deltaRatioPrev: outcomesByClusterRatio,
    }

    const clustersPaths = getClustersPaths(optimalWaves)
    const groupedOutcomes = getGroupedOutcomes(clustersPaths, t.stats.waveClusterOutcomes)

    const clusterExpectancy = calculateClusterFadeExpectancy(optimalStats, groupedOutcomes, sequenceStats, optimalWaves, {
      commission: (marketFee * 2) * (1 - cashbackRate), // 0.002,
      averagingMode: BacktestSettings.expectancyMode.value,
      takeShift: BacktestSettings.expectancyTakeShift,
      clusterPathIndex: 3,
      stopMultiplier: BacktestSettings.expectancyStopMultiplier,
      enterDistMultIndex: 2,
      exitDistMultIndex: 2,
    })
    t.clusterExpectancy = clusterExpectancy


    calcCache[optimalWaves.length] = {
      waveClusterStatsPercent,
      waveClusterStatsRatio,
      clusterExpectancy,
    }
  } else {
    const cache = calcCache[optimalWaves.length]
    waveClusterStatsPercent = cache.waveClusterStatsPercent
    waveClusterStatsRatio = cache.waveClusterStatsRatio
    t.clusterExpectancy = cache.clusterExpectancy
  }

  // *************************************
  // OTHER
  // *************************************

  // const currentCategory = `${lastWaveUpdated.type || lastWave.type || 'none'}${capitalizeFirstLetter(lastWaveUpdated.direction  || lastWave.direction || 'none')}`
  const currentCategory = `${prevLastWave?.type || 'none'}${capitalizeFirstLetter(prevLastWave?.direction || 'none')}`
  t.currentCategory = currentCategory

  // ************************
  // RATIOS
  const pullbackRatio = waveClusterStatsRatio?.pullback?.[currentCategory]?.frequency
    ? waveClusterStatsRatio?.pullback?.[currentCategory]
    : waveClusterStatsRatio?.pullback?.any //waveRatioStats?.[ratioTypeCurrent][currentCategory]
  t.stats.pullbackRatio = pullbackRatio
  const pullbackRatioAvg = pullbackRatio?.stats?.avg
  const pullbackRatioMedian = pullbackRatio?.stats?.median

  const moveRatio = waveClusterStatsRatio?.move?.[currentCategory]?.frequency
    ? waveClusterStatsRatio?.move?.[currentCategory]
    : waveClusterStatsRatio?.move?.any //waveRatioStats?.[ratioTypeCurrent][currentCategory]
  t.stats.moveRatio = moveRatio
  const moveRatioAvg = moveRatio?.stats?.avg
  const moveRatioMedian = moveRatio?.stats?.median

  // ************************
  // Targets PrevWave
  const pullbackTargetDeltaPrevWave = lastRealWave?.delta * (Math.min(pullbackRatioAvg, pullbackRatioMedian) || 0.61)
  const pullbackTargetDeltaPrevWavePrice = lastRealWave?.direction === 'up'
    ? lastRealWave?.high - pullbackTargetDeltaPrevWave
    : lastRealWave?.low + pullbackTargetDeltaPrevWave

  t.pullbackTargetDeltaPrevWave = {
    delta: pullbackTargetDeltaPrevWave,
    price: pullbackTargetDeltaPrevWavePrice,
    up: lastRealWave?.high - pullbackTargetDeltaPrevWave,
    down: lastRealWave?.low + pullbackTargetDeltaPrevWave,
  }

  const moveTargetDeltaPrevWave = lastRealWave?.delta * (Math.min(moveRatioAvg, moveRatioMedian) || 1.22)
  const moveTargetDeltaPrevWavePrice = lastRealWave?.direction === 'up'
    ? lastRealWave?.high - moveTargetDeltaPrevWave
    : lastRealWave?.low + moveTargetDeltaPrevWave

  t.moveTargetDeltaPrevWave = {
    delta: moveTargetDeltaPrevWave,
    price: moveTargetDeltaPrevWavePrice,
    up: lastRealWave?.high - moveTargetDeltaPrevWave,
    down: lastRealWave?.low + moveTargetDeltaPrevWave,
  }

  // ************************
  // Targets LastWaveUpdated
  const pullbackTargetDeltaLastWaveUpdated = lastWaveUpdated?.delta * (Math.min(pullbackRatioAvg, pullbackRatioMedian) || 0.61)
  const pullbackTargetDeltaLastWaveUpdatedPrice = lastWaveUpdated?.direction === 'up'
    ? lastWaveUpdated?.high - pullbackTargetDeltaLastWaveUpdated
    : lastWaveUpdated?.low + pullbackTargetDeltaLastWaveUpdated

  t.pullbackTargetDeltaLastWaveUpdated = {
    delta: pullbackTargetDeltaLastWaveUpdated,
    price: pullbackTargetDeltaLastWaveUpdatedPrice,
    up: lastRealWave?.high - pullbackTargetDeltaLastWaveUpdated,
    down: lastRealWave?.low + pullbackTargetDeltaLastWaveUpdated,
  }

  const moveTargetDeltaLastWaveUpdated = lastWaveUpdated?.delta * (Math.min(moveRatioAvg, moveRatioMedian) || 1.22)
  const moveTargetDeltaLastWaveUpdatedPrice = lastWaveUpdated?.direction === 'up'
    ? lastWaveUpdated?.high - moveTargetDeltaLastWaveUpdated
    : lastWaveUpdated?.low + moveTargetDeltaLastWaveUpdated

  t.moveTargetDeltaLastWaveUpdated = {
    delta: moveTargetDeltaLastWaveUpdated,
    price: moveTargetDeltaLastWaveUpdatedPrice,
    up: lastRealWave?.high - moveTargetDeltaLastWaveUpdated,
    down: lastRealWave?.low + moveTargetDeltaLastWaveUpdated,
  }

  // ************************
  // OTHER
  const levelUp = t.closestHighs[t.closestHighs.length - 1]
  const lastWaveDeltaUp = levelUp?.high - levelUp?.low
  const waveTargetDeltaUp = lastWaveDeltaUp * (Math.min(pullbackRatioAvg, pullbackRatioMedian) || pullbackRatioAvg || pullbackRatioMedian)
  t.waveTargetDeltaUp = waveTargetDeltaUp

  const levelDown = t.closestLows[t.closestLows.length - 1]
  const lastWaveDeltaDown = levelDown?.high - levelDown?.low
  const waveTargetDeltaDown = lastWaveDeltaDown * Math.min(pullbackRatioAvg, pullbackRatioMedian)
  t.waveTargetDeltaDown = waveTargetDeltaDown

  const clusterTargetsUp = []
  const clusterTargetsDown = []
  const pullbackRatioClusters = pullbackRatio?.clusters || []
  for (const cluster of pullbackRatioClusters) {
    const targetUp = lastWaveDeltaUp * cluster?.min
    clusterTargetsUp.push(targetUp)
    const targetDown = lastWaveDeltaDown * cluster?.min
    clusterTargetsDown.push(targetDown)
  }
  t.clusterTargetsUp = clusterTargetsUp
  t.clusterTargetsDown = clusterTargetsDown

  return {
    calc: t,
    predictedCurrentWaveData,
  }
}

// ****************************
// ENTERS
// ****************************

export function checkEnterLong(reverse, candles, props, state, indicators, hasChanges, symbolInfo, backtestSettings, expectancyDebug, portfolioManager) {
  const candle = candles[candles.length - 1]
  const prevCandle = candles[candles.length - 2]
  const selectedProps = state.propsLong ? state.propsLong : props
  // const { blendedExpectancy } = props

  const {
    calc,
    optimalWaves,
    lastRealWave,
    lastWaveUpdated,
  } = selectedProps

  const {
    closestHighs, closestLows,
    closestHighsPrices, closestLowsPrices,
    closestHighsPricesRev, closestLowsPricesRev,
    //
    bounceEnterLong, bounceEnterShort,
    bounceSLPercentLong, bounceSLPercentShort,
    clusterTargetsUp, clusterTargetsDown,
    waveTargetDeltaUp, waveTargetDeltaDown,
    waveTargetPercent,
    //
    pullbackTargetDeltaPrevWave,
    moveTargetDeltaPrevWave,
    pullbackTargetDeltaLastWaveUpdated,
    moveTargetDeltaLastWaveUpdated,
    //
    predictedWave,
    predictedCurrentWave,
    expectancies,
    clusterExpectancy,
  } = calc

  // console.log('checkEnter', selectedProps, hasChanges)

  const prevExpectancy = expectancies?.prevLong?.blendedExpectancy

  const currentExpectancyShort = expectancies?.currentShort?.blendedExpectancy
  const currentExpectancyLong = expectancies?.currentLong?.blendedExpectancy
  const currentExpectancy = currentExpectancyLong

  const useClusterExpectancy = clusterExpectancy.currentPullbackRatio.length || clusterExpectancy.currentMoveRatio.length
  const combinedClusters = [...clusterExpectancy.currentPullbackRatio, ...clusterExpectancy.currentMoveRatio]

  // currentClusterExpectancy.sort((a, b) => a.cluster.initial.prob - b.cluster.initial.prob)
  const selectedClusterExpectancy = clusterExpectancy.currentPullbackRatio[0] // combinedClusters
  const selectedExpectancy = useClusterExpectancy ? selectedClusterExpectancy : currentExpectancy

  const filtersDefaultState = false

  const priceFilter = symbolInfo?.filters?.find(f => f.filterType === 'PRICE_FILTER')
  const tickSize = parseFloat(priceFilter?.tickSize || candle.close / 1000)

  const drawPoints = {
    bounce: {
      long: [],
      short: [],
    },
  }

  const levelL1 = closestLowsPrices[closestLowsPrices.length - 1]
  const levelH1 = closestHighsPrices[closestHighsPrices.length - 1]
  // const levelL1Rev = closestLowsPricesRev[closestLowsPricesRev.length - 1]
  // const levelH1Rev = closestHighsPricesRev[closestHighsPricesRev.length - 1]

  const prevWave = optimalWaves[optimalWaves.length - 2]
  // const enter1 = prevWave.high - prevWave.deltaPercent * prevExpectancy?.targetRatio1
  // const enter1 = prevWave.high - prevWave.high * prevWave.deltaPercent * selectedExpectancy?.enterRatio
  const enter1 = prevWave.high - prevWave.high * selectedExpectancy?.enter
  // const enter2 = prevWave.high - prevWave.deltaPercent * prevExpectancy?.targetRatio2

  // console.log('DEBUG', enter1, enter2, prevWave.high, prevWave)
  // *****************************************************
  // LONG
  // *****************************************************
  const enterLong = bounceEnterLong?.optimal?.limit
  const slPercentLong = bounceSLPercentLong?.optimal?.limit
  const hasAllParamsLong = enterLong && slPercentLong && waveTargetDeltaUp//waveTargetDeltaDown

  state.enteredLong = state.enteredLong || []

  let longFilterAllow = filtersDefaultState

  // const target1Price = levelL1 + levelL1 * lastWaveUpdated.deltaPercent * selectedExpectancy?.targetRatio1
  const target1Price = levelL1 + levelL1 * selectedExpectancy?.target1
  const isNextTargetOk1 = target1Price / prevCandle.close > 1 + minTargetPercent
  const isNextTargetOk2 = pullbackTargetDeltaLastWaveUpdated.price / prevCandle.close > 1 + minTargetPercent
  const isNextTargetOk = true // isNextTargetOk1 // && isNextTargetOk2
  const isCurrentPriceOk = true // prevCandle.low <= pullbackTargetDeltaPrevWave.price || candle.low <= pullbackTargetDeltaPrevWave.price

  const isIgnoreOk = !(longIgnoreEnabled && state?.ignoreLong === optimalWaves.length)
  const isSequenceOk = true
    && predictedWave?.direction === 'up'
    && predictedCurrentWave?.type === 'pullback'
    && predictedWave?.type === 'move'

  const isIndicatorOK = true
  // && indicators?.['CCI_42']?.value >= 0
  // && indicators?.['ZeroLagChaikinOscillator_44_111']?.value >= 0
  // && indicators?.['ZeroLagChaikinOscillator_44_111']?.value > indicators?.['ZeroLagChaikinOscillator_66_155']?.value
  // indicators?.['CCI_42']?.value >= 0 // 'PSAR_0.002_0.007_0.2' // indicators?.['Stochastic_64_256']?.value?.k >= 25


  const isMinExpectancyOK = currentExpectancy?.netExpectancy > backtestSettings.minExpectancy //0.0015
  const isBestSide = true //currentExpectancyShort?.netExpectancy >= currentExpectancyLong?.netExpectancy

  const longFilter = true
    && isNextTargetOk && isCurrentPriceOk
    && isIndicatorOK && isIgnoreOk
    && isSequenceOk
    && isMinExpectancyOK && isBestSide


  if (longFilter) {
    longFilterAllow = true
  }

  // console.log('enter long check1', state.inLong, state.entryPointsLong, state.enteredLong, )
  const maxTradesReached = (state?.actionsLong?.length || 0) > backtestSettings.tradesLimit

  const longPositionCheck = !state.inLong || state.longPosition === 0 // !state.inLong // || (state.inLong && state.entryPointsLong.length)
  const allowEnterLong = backtestSettings.enableLong
    && longFilterAllow && longPositionCheck && hasAllParamsLong
    && !maxTradesReached
  // && closestLowsPrices.length && closestHighsPrices.length


  function getClusterGroupPoints(group, groupId, groupWeight = 1) {
    const entryPoints = []

    let minSL = Infinity
    let minTSL = Infinity
    let maxTSLDev = -Infinity

    const clusterWeights = calculateClusterWeights(group, BacktestSettings.clusterWeightsMode.value, groupWeight) // 'netExpectancy' , 'prob', etc

    for (let i = 0; i < group.length; i++) {
      const cluster = group[i]

      const enterLevel = prevWave.high - prevWave.high * cluster?.enter
      const enters = getClusterEnters({
        direction: 'long',
        clusterWeight: clusterWeights.weights[i],
        percentToLvl: cluster.enterDev, //selectedExpectancy?.stopLoss, //enterLong,
        percentToStop: cluster.enterDev, //selectedExpectancy?.stopLoss, //enterLong, //slPercentLong,
        lvlPrice: enterLevel, //levelL1, //candle.close,
      })

      const tp1 = enterLevel + enterLevel * cluster.target1
      const tp2 = enterLevel + enterLevel * cluster.target2

      const sl = enterLevel - enterLevel * cluster.stopLoss
      const tsl = enterLevel - enterLevel * cluster.trailingSL

      if (sl < minSL) minSL = sl
      if (tsl < minTSL) minTSL = tsl
      if (cluster.trailingSL > maxTSLDev) maxTSLDev = cluster.trailingSL


      entryPoints.push({
        clusterId: `${groupId}_${i}`,
        cluster,
        level: enterLevel,
        enterDev: cluster.enterDev,
        enters,
        index: i,
        total: combinedClusters.length,
        clusterWeight: clusterWeights.weights[i],
        tp1,
        tp2,
        sl,
        tsl
      })
    }

    entryPoints.forEach(point => {
      point.minSL = minSL // Math.min(minSL, minTSL)
      point.minTSL = minTSL
      point.maxTSLDev = maxTSLDev
    })

    return entryPoints
  }

  if (allowEnterLong) {

    // const enter = level.low - level.low * enterLong
    // const levelPrice = levelL1

    // const entryPoints = getBounceEnters({
    //   direction: 'long',
    //   percentToLvl: enterLong,
    //   percentToStop: slPercentLong,
    //   lvlPrice: levelPrice,
    // })

    // if (candle?.close && !state.inLong) {
    //   entryPoints = getBounceEnters({
    //     direction: 'long',
    //     percentToLvl: selectedClusterExpectancy.enterDev, //selectedExpectancy?.stopLoss, //enterLong,
    //     percentToStop: selectedClusterExpectancy.enterDev, //selectedExpectancy?.stopLoss, //enterLong, //slPercentLong,
    //     lvlPrice: enter1, //levelL1, //candle.close,
    //   })
    //   // console.log('entryPoints', entryPoints, pullbackTargetDeltaLastWaveUpdated.price, candle.close, pullbackTargetDeltaLastWaveUpdated.price / candle.close, enterLong, slPercentLong)
    // }


    if (candle?.close && !state.inLong) {
      const entryPoints = []

      // const cprWeights = calculateClusterWeights(clusterExpectancy.currentPullbackRatio, BacktestSettings.clusterWeightsMode.value)
      // const cmrWeights = calculateClusterWeights(clusterExpectancy.currentMoveRatio, BacktestSettings.clusterWeightsMode.value)
      // const totalWeights = cprWeights.total + cmrWeights.total

      const cprPoints = getClusterGroupPoints(clusterExpectancy.currentPullbackRatio, 'currentPullback', 0.5) //cprWeights.total / totalWeights
      entryPoints.push(...cprPoints)

      const cmrPoints = getClusterGroupPoints(clusterExpectancy.currentMoveRatio, 'currentMove', 0.5) //  cmrWeights.total / totalWeights
      entryPoints.push(...cmrPoints)

      console.log('setEntryPointsLong', entryPoints)

      state.entryPointsLong = entryPoints || []
      state.enteredLong = []
      state.enteredClustersLong = []

      // console.log('entryPoints', entryPoints, pullbackTargetDeltaLastWaveUpdated.price, candle.close, pullbackTargetDeltaLastWaveUpdated.price / candle.close, enterLong, slPercentLong)
    }

    // console.log('enter long check2', state.inLong, state.entryPointsLong, state.enteredLong, entryPoints)
    // console.log('enter long check', candle.high > enter, candle.high, enter, level, inLong, enterLong, closestHighs.length)

  }


  for (let c = 0; c < state.entryPointsLong.length; c++) {
    const clusterPoints = state.entryPointsLong[c]
    if (!clusterPoints) continue

    const {
      clusterId, enters,
      cluster, enterDev, level,
      clusterWeight, index, total,
      tp1, tp2,
      sl, tsl,
      minSL, minTSL, maxTSLDev,
    } = clusterPoints

    for (let i = 0; i < enters.length; i++) {
      const { price, weight } = enters[i]

      if (candle.low <= price && candle.high >= price) {
        if (!state.inLong) {
          state.longEntersNumber = state.longEntersNumber ? state.longEntersNumber + 1 : 1

          state.inLong = true
          state.propsLong = props

          state.tpsLong = [
            enter1 + enter1 * selectedExpectancy?.target1,
            enter1 + enter1 * selectedExpectancy?.target2,
          ]
          state.triggeredTPsLong = []
          state.enteredLong = []

          state.actionsLong.push({
            entries: [],
            exits: [],
            // debug info
            simpleExpectancy: currentExpectancy?.netExpectancy || 0,
            selectedProps,
            // indicators: getIndicatorValues(indicators),
          })

          state.longLvlPrice = levelL1
          state.longLevelL1 = levelL1
          state.longLevelH1 = levelH1
          // state.longLevelL1Rev = levelL1Rev
          // state.longLevelH1Rev = levelH1Rev
          state.longEnterPrice = price

        }

        state.ignoreLong = optimalWaves.length

        try {
          // if (!state?.enteredLong?.length) {
          //   console.log('enter long invalid enteredLong')
          // }
          state.enteredLong.push({ ...enters[i], clusterId })
          const point = {
            time: candle.time,
            value: price,
            isFirstPoint: !state.inLong,
          }
          drawPoints.bounce.long.push(point)
          enters.splice(i, 1)
        } catch (error) {
          console.log('enter long invalid enteredLong', state.enteredLong, error)
          break
        }

        const avgEntrySumm = state.enteredLong.reduce((acc, entry) => acc + entry.price * entry.weight, 0)
        const totalWeight = state.enteredLong.reduce((acc, entry) => acc + entry.weight, 0)
        const avgEntryPrice = avgEntrySumm / totalWeight

        const selectedPrice = avgEntryPrice // levelPrice // Math.min(levelPrice, avgEntryPrice)

        // **************************
        // STOP LOSSES

        // let minSL = Infinity
        // let minTSL = Infinity
        // let maxTSLDev = -Infinity
        // for (const cluster of combinedClusters) {
        //   // const sl = enter1 - enter1 * cluster.stopLoss
        //   // const tsl = enter1 - enter1 * cluster.trailingSL
        //   const enter = prevWave.high - prevWave.high * cluster?.enter
        //   const sl = enter - enter * cluster.stopLoss
        //   const tsl = enter - enter * cluster.trailingSL
        //   if (sl < minSL) minSL = sl
        //   if (tsl < minTSL) minTSL = tsl
        //   if (cluster.trailingSL > maxTSLDev) maxTSLDev = cluster.trailingSL
        // }

        // let minSL = enter1 - enter1 * selectedClusterExpectancy.stopLoss
        // let minTSL = enter1 - enter1 * selectedClusterExpectancy.trailingSL
        // let maxTSLDev = selectedClusterExpectancy.trailingSL


        // const slPercentLong = bounceSLPercentLong?.optimal?.limit
        // const sl = selectedPrice - selectedPrice * slPercentLong

        // const sl2 = lastRealWave?.type === 'move'
        //   && avgEntryPrice > (lastRealWave?.low + candle.high - candle.low)
        //   ? lastRealWave?.low
        //   : lastRealWave.high - lastRealWave.high * 0.004
        // const sl3 = levelL1 - levelL1 * slPercentLong

        // const slExpPercent = prevWave.high - prevWave.high * selectedExpectancy?.stopLoss

        const slExpSelected = useClusterExpectancy ? minSL : selectedExpectancy?.stopLoss
        const tslExpSelected = useClusterExpectancy ? minTSL : selectedExpectancy?.trailingSL

        // state.slLong = slExpPercent // selectedPrice > levelL1 ? levelL1 : slExp //Math.max(sl)//, sl2)
        // state.slLong = enter1 - enter1 * slExpSelected
        state.slLong = useClusterExpectancy ? slExpSelected : enter1 - enter1 * slExpSelected
        state.slLong -= tickSize

        state.tslLong = useClusterExpectancy ? tslExpSelected : state.slLong - state.slLong * tslExpSelected
        state.tslDeltaLong = maxTSLDev

        state.slsLong = []

        // console.log('sl long', levelL1, sl, sl2, state.slsLong, bounceSLPercentLong)

        // **************************
        // TAKE PROFITS

        state.tpTargetLong = selectedPrice + selectedPrice * waveTargetPercent
        // state.tpLong = levelPrice + waveTargetDeltaUp //waveTargetDeltaDown
        // state.tpLong = selectedPrice + selectedPrice * slPercentLong
        // state.tpsLong = clusterTargetsUp.map(target => selectedPrice + target)
        // state.tpsLong = [pullbackTargetDeltaLastWaveUpdated.price]

        // state.tpsLong = [
        //   // tp1,
        //   // tp2,
        //   enter1 + enter1 * selectedExpectancy?.target1,
        //   enter1 + enter1 * selectedExpectancy?.target2,
        //   // enter1 + enter1 * lastWaveUpdated.deltaPercent * selectedExpectancy?.targetRatio1,
        //   // enter1 + enter1 * lastWaveUpdated.deltaPercent * selectedExpectancy?.targetRatio2,
        //   // selectedPrice + blendedExpectancy?.target1,
        //   // selectedPrice + blendedExpectancy?.target2
        // ]

        // state.tpsLong = []
        // for (const cluster of combinedClusters) {
        //   // const sl = enter1 - enter1 * cluster.stopLoss
        //   // const tsl = enter1 - enter1 * cluster.trailingSL
        //   const enter = prevWave.high - prevWave.high * cluster?.enter
        //   const t1 = enter + enter * cluster.target1
        //   const t2 = enter + enter * cluster.target2
        //   state.tpsLong.push(t1, t2)
        // }
        if (!state.tpsLong.includes(tp1)) {
          state.tpsLong.push(tp1)
        }

        if (!state.tpsLong.includes(tp2)) {
          state.tpsLong.push(tp2)
        }

        state.tpsLong.sort((a, b) => a - b)


        const positionSizeDelta = state.balance * positionPercent * weight
        const fee = positionSizeDelta * limitFee
        const deltaGross = -positionSizeDelta
        const deltaNet = deltaGross - fee + fee * cashbackRate

        state.longPosition += positionSizeDelta

        state.balance += deltaNet
        state.balanceLong += deltaNet

        state.feeTotal += fee
        state.feeLong += fee

        // if (!state?.enteredLong?.length) {
        //   console.log('enter long invalid enteredLong')
        // }
        // state.enteredLong.push(state.entryPointsLong[i])
        state.entryPointsLong.splice(c, 1)

        if (state.actionsLong[state.actionsLong.length - 1]?.entries?.length) {
          state.actionsLong[state.actionsLong.length - 1]?.entries.push({
            clusterId,
            type: 'enter',
            time: candle.time,
            value: price,
            trade: {
              fee,
              deltaGross,
              deltaNet,
              positionSize: state.longPosition,
              positionDelta: positionSizeDelta,
              avgEntryPrice,
              metadata: {
                expectancy: currentExpectancy?.netExpectancy,
                // expectancy: selectedExpectancy.netExpectancy,
                // currentExpectancy: currentExpectancy?.netExpectancy,
              },
            }
          })
        } else {
          state.actionsLong[state.actionsLong.length - 1]?.entries.push({
            clusterId,
            type: 'enter',
            time: candle.time,
            value: price,
            trade: {
              lvlPrice: state.longLvlPrice,
              levelH1: state.longLevelH1,
              levelL1: state.longLevelL1,
              levelH1Rev: state.longLevelH1Rev,
              levelL1Rev: state.longLevelL1Rev,
              enterPrice: state.longEnterPrice,
              sl: state.slLong,
              sls: state.slsLong,
              tp: state.tpLong,
              tps: [...state.tpsLong],
              tpTarget: state.tpTargetLong,
              entryPointsLong: [...state.entryPointsLong],
              enteredLong: [...state.enteredLong],
              avgEntryPrice,
              pullbackTargetDeltaLastWaveUpdatedPrice: pullbackTargetDeltaLastWaveUpdated.price,
              moveTargetDeltaLastWaveUpdatedPrice: moveTargetDeltaLastWaveUpdated.price,
              pullbackTargetDeltaPrevWavePrice: pullbackTargetDeltaPrevWave.price,
              moveTargetDeltaPrevWavePrice: moveTargetDeltaPrevWave.price,
              fee,
              deltaGross,
              deltaNet,
              positionSize: state.longPosition,
              positionDelta: positionSizeDelta,
              metadata: {
                expectancy: currentExpectancy.netExpectancy,
                // expectancy: selectedExpectancy.netExpectancy,
                // currentExpectancy: currentExpectancy?.netExpectancy,
              },
            }
          })

          expectancyDebug.push({
            clusterExpectancy,
            optimalWaves: optimalWaves.slice(-3),
          })
        }

        if (logEnters) {
          const stateClone = JSON.parse(JSON.stringify(state))
          console.log('enter long', state.balance, deltaNet, price, stateClone, candle)
        }
      }
    }

  }

  return drawPoints
}

export function checkEnterShort(reverse, candles, props, state, indicators, hasChanges, symbolInfo, backtestSettings, expectancyDebug) {
  const candle = candles[candles.length - 1]
  const prevCandle = candles[candles.length - 2]
  const selectedProps = state.propsShort ? state.propsShort : props
  const {
    calc,
    optimalWaves,
    lastRealWave,
    lastWaveUpdated,
  } = selectedProps

  const {
    closestHighs, closestLows,
    closestHighsPrices, closestLowsPrices,
    closestHighsPricesRev, closestLowsPricesRev,
    //
    bounceEnterLong, bounceEnterShort,
    bounceSLPercentLong, bounceSLPercentShort,
    clusterTargetsUp, clusterTargetsDown,
    waveTargetDeltaUp, waveTargetDeltaDown,
    waveTargetPercent,
    //
    pullbackTargetDeltaPrevWave,
    moveTargetDeltaPrevWave,
    pullbackTargetDeltaLastWaveUpdated,
    moveTargetDeltaLastWaveUpdated,
    //
    predictedWave,
    predictedCurrentWave,
    expectancies,
    clusterExpectancy,

  } = calc

  const prevExpectancy = expectancies?.prevShort?.blendedExpectancy

  const currentExpectancyShort = expectancies?.currentShort?.blendedExpectancy
  const currentExpectancyLong = expectancies?.currentLong?.blendedExpectancy
  const currentExpectancy = currentExpectancyShort

  const useClusterExpectancy = clusterExpectancy.currentPullbackRatio.length || clusterExpectancy.currentMoveRatio.length
  const combinedClusters = [...clusterExpectancy.currentPullbackRatio, ...clusterExpectancy.currentMoveRatio]

  // currentClusterExpectancy.sort((a, b) => a.cluster.initial.prob - b.cluster.initial.prob)
  const selectedClusterExpectancy = combinedClusters[0]
  const selectedExpectancy = useClusterExpectancy ? selectedClusterExpectancy : currentExpectancy


  const filtersDefaultState = false

  const priceFilter = symbolInfo?.filters?.find(f => f.filterType === 'PRICE_FILTER')
  const tickSize = parseFloat(priceFilter?.tickSize || candle.close / 1000)

  const drawPoints = {
    bounce: {
      long: [],
      short: [],
    },
  }

  const levelL1 = closestLowsPrices[closestLowsPrices.length - 1]
  const levelH1 = closestHighsPrices[closestHighsPrices.length - 1]
  // const levelL1Rev = closestLowsPricesRev[closestLowsPricesRev.length - 1]
  // const levelH1Rev = closestHighsPricesRev[closestHighsPricesRev.length - 1]

  const prevWave = optimalWaves[optimalWaves.length - 2]
  // const enter1 = prevWave.high - prevWave.deltaPercent * prevExpectancy?.targetRatio1
  // const enter1 = prevWave.high - prevWave.high * prevWave.deltaPercent * currentExpectancy?.enterRatio
  const enter1 = prevWave.low + prevWave.low * currentExpectancy?.enter
  // const enter2 = prevWave.high - prevWave.deltaPercent * prevExpectancy?.targetRatio2
  // *****************************************************
  // SHORT
  // *****************************************************
  const enterShort = bounceEnterShort?.optimal?.limit
  const slPercentShort = bounceSLPercentShort?.optimal?.limit
  const hasAllParamsShort = enterShort && slPercentShort && waveTargetDeltaDown

  state.enteredShort = state.enteredShort || []

  let shortFilterAllow = filtersDefaultState

  // const target1Price = levelL1 + levelL1 * lastWaveUpdated.deltaPercent * currentExpectancy?.targetRatio1
  const target1Price = levelH1 - levelH1 * currentExpectancy?.target1
  const isNextTargetOk1 = prevCandle.close / target1Price > 1 + minTargetPercent
  const isNextTargetOk2 = prevCandle.close / pullbackTargetDeltaLastWaveUpdated.price > 1 + minTargetPercent
  const isNextTargetOk = true // isNextTargetOk1 // && isNextTargetOk2

  const isCurrentPriceOk = true // prevCandle.high >= pullbackTargetDeltaPrevWave.price || candle.high >= pullbackTargetDeltaPrevWave.price
  const isIgnoreOk = !(shortIgnoreEnabled && state?.ignoreShort === optimalWaves.length)
  const isSequenceOk = true
    && predictedWave?.direction === 'down'
    && predictedCurrentWave?.type === 'pullback'
    && predictedWave?.type === 'move'

  const isIndicatorOK = true
  // && indicators["Stochastic_42_144"]?.value?.k > 80
  // && indicators?.['CCI_42']?.value <= 0 
  // && indicators?.['CCI_42']?.value >= 0 
  // && indicators?.['Stochastic_64_256']?.value?.k >= 25
  // && indicators["RSI_avg4_42"]?.value < 40

  const isMinExpectancyOK = currentExpectancy?.netExpectancy > backtestSettings.minExpectancy //0.0015
  const isBestSide = true //currentExpectancyShort?.netExpectancy >= currentExpectancyLong?.netExpectancy

  const shortFilter = true
    && isNextTargetOk && isCurrentPriceOk
    && isIndicatorOK && isIgnoreOk
    && isSequenceOk
    && isMinExpectancyOK && isBestSide

  if (shortFilter) {
    shortFilterAllow = true
  }

  const maxTradesReached = Math.floor((state?.actionsShort?.length || 0) / 2) > backtestSettings.tradesLimit

  const shortPositionCheck = !state.inShort || state.shortPosition === 0  //|| (state.inShort && state.entryPointsShort.length)
  const allowEnterShort = backtestSettings.enableShort
    && shortFilterAllow && shortPositionCheck && hasAllParamsShort
    && closestLowsPrices.length && closestHighsPrices.length
    && !maxTradesReached

  if (allowEnterShort) {
    // const level = closestHighs[closestHighs.length - 1]
    // const realLevelPrice = level.high
    // const level = closestHighsPrices[closestHighsPrices.length - 1]
    // const enter = realLevelPrice - realLevelPrice * enterShort

    // const levelPrice = levelH1

    // const entryPoints = getBounceEnters({
    //   direction: 'short',
    //   percentToLvl: enterShort,
    //   percentToStop: slPercentShort,
    //   lvlPrice: levelPrice,
    // })




    // if (candle?.close && !state.inShort) {
    //   entryPoints = getClusterEnters({
    //     direction: 'short',
    //     percentToLvl: selectedClusterExpectancy.enterDev, //currentExpectancy?.stopLoss, //enterShort,
    //     percentToStop: selectedClusterExpectancy.enterDev, //currentExpectancy?.stopLoss, //enterShort, //slPercentLong,
    //     lvlPrice: enter1 //candle.close,
    //   })
    //   // console.log('entryPoints', entryPoints, pullbackTargetDeltaLastWaveUpdated.price, candle.close, pullbackTargetDeltaLastWaveUpdated.price / candle.close, enterLong, slPercentLong)
    // }


    function getClusterGroupPoints(group, groupId, groupWeight = 1) {
      const entryPoints = []

      let maxSL = -Infinity
      let maxTSL = -Infinity
      let maxTSLDev = -Infinity

      const clusterWeights = calculateClusterWeights(group, BacktestSettings.clusterWeightsMode.value, groupWeight) // 'netExpectancy' , 'prob', etc

      for (let i = 0; i < group.length; i++) {
        const cluster = group[i]

        const enterLevel = prevWave.low + prevWave.low * cluster?.enter
        const enters = getClusterEnters({
          direction: 'short',
          clusterWeight: clusterWeights.weights[i],
          percentToLvl: cluster.enterDev, //selectedExpectancy?.stopLoss, //enterLong,
          percentToStop: cluster.enterDev, //selectedExpectancy?.stopLoss, //enterLong, //slPercentLong,
          lvlPrice: enterLevel, //levelL1, //candle.close,
        })

        const tp1 = enterLevel - enterLevel * cluster.target1
        const tp2 = enterLevel - enterLevel * cluster.target2

        const sl = enterLevel + enterLevel * cluster.stopLoss
        const tsl = enterLevel + enterLevel * cluster.trailingSL

        if (sl > maxSL) maxSL = sl
        if (tsl > maxTSL) maxTSL = tsl
        if (cluster.trailingSL > maxTSLDev) maxTSLDev = cluster.trailingSL


        entryPoints.push({
          clusterId: `${groupId}_${i}`,
          cluster,
          level: enterLevel,
          enterDev: cluster.enterDev,
          enters,
          index: i,
          total: combinedClusters.length,
          clusterWeight: clusterWeights.weights[i],
          tp1,
          tp2,
          sl,
          tsl
        })
      }

      entryPoints.forEach(point => {
        point.maxSL = maxSL // Math.min(maxSL, maxTSL)
        point.maxTSL = maxTSL
        point.maxTSLDev = maxTSLDev
      })

      return entryPoints
    }

    if (candle?.close && !state.inShort) {
      const entryPoints = []

      // const cprWeights = calculateClusterWeights(clusterExpectancy.currentPullbackRatio, BacktestSettings.clusterWeightsMode.value)
      // const cmrWeights = calculateClusterWeights(clusterExpectancy.currentMoveRatio, BacktestSettings.clusterWeightsMode.value)
      // const totalWeights = cprWeights.total + cmrWeights.total

      const cprPoints = getClusterGroupPoints(clusterExpectancy.currentPullbackRatio, 'currentPullback', 0.5) //cprWeights.total / totalWeights
      entryPoints.push(...cprPoints)

      const cmrPoints = getClusterGroupPoints(clusterExpectancy.currentMoveRatio, 'currentMove', 0.5) //  cmrWeights.total / totalWeights
      entryPoints.push(...cmrPoints)

      state.entryPointsShort = entryPoints || []
      state.enteredShort = []
      state.enteredClustersShort = []

      // console.log('entryPoints', entryPoints, pullbackTargetDeltaLastWaveUpdated.price, candle.close, pullbackTargetDeltaLastWaveUpdated.price / candle.close, enterLong, slPercentLong)
    }

    // console.log('enter long check', candle.high > enter, candle.high, enter, level, inLong, enterLong, closestHighs.length)

  }

  // if (allowEnterShort) {
  for (const clusterPoints of state.entryPointsShort) {
    const {
      clusterId, enters,
      cluster, enterDev, level,
      clusterWeight, index, total,
      tp1, tp2,
      sl, tsl,
      maxSL, maxTSL, maxTSLDev,
    } = clusterPoints

    for (let i = 0; i < enters.length; i++) {
      const { price, weight } = enters[i]

      if (candle.low <= price && candle.high >= price) {
        if (!state.inShort) {
          state.shortEntersNumber = state.shortEntersNumber ? state.shortEntersNumber + 1 : 1

          state.inShort = true
          state.propsShort = props

          state.tpsShort = [
            // enter1 - enter1 * selectedExpectancy?.target1,
            // enter1 - enter1 * selectedExpectancy?.target2,
          ]
          state.triggeredTPsShort = []
          state.enteredShort = []

          state.actionsShort.push({
            entries: [],
            exits: [],
            // debug info
            simpleExpectancy: currentExpectancy?.netExpectancy || 0,
            selectedProps,
            // indicators: getIndicatorValues(indicators),
          })

          state.shortLvlPrice = levelH1
          state.shortLevelL1 = levelL1
          state.shortLevelH1 = levelH1
          // state.shortLevelL1Rev = levelL1Rev
          // state.shortLevelH1Rev = levelH1Rev
          state.shortEnterPrice = price


        }

        state.ignoreShort = optimalWaves.length

        try {
          // if (!state?.enteredShort?.length) {
          //   console.log('enter short invalid enteredShort')
          // }
          state.enteredShort.push({ ...enters[i], clusterId })
          const point = {
            time: candle.time,
            value: price,
            isFirstPoint: !state.inShort,
          }
          drawPoints.bounce.short.push(point)
          enters.splice(i, 1)
        } catch (error) {
          console.log('enter short invalid enteredShort', state.enteredShort, error)
          break
        }

        const avgEntrySumm = state.enteredShort.reduce((acc, entry) => acc + entry.price * entry.weight, 0)
        const totalWeight = state.enteredShort.reduce((acc, entry) => acc + entry.weight, 0)
        const avgEntryPrice = avgEntrySumm / totalWeight

        const selectedPrice = avgEntryPrice // levelPrice // Math.min(levelPrice, avgEntryPrice)

        // **************************
        // STOP LOSSES

        // const slPercentShort = bounceSLPercentShort?.optimal?.limit
        // const sl = selectedPrice + selectedPrice * slPercentShort
        // const sl2 = lastRealWave?.type === 'move'
        //   && avgEntryPrice < (lastRealWave?.high + candle.high - candle.low)
        //   ? lastRealWave?.high
        //   : lastRealWave.low - lastRealWave.low * 1.004


        const slExpSelected = useClusterExpectancy ? maxSL : selectedExpectancy?.stopLoss
        const tslExpSelected = useClusterExpectancy ? maxTSL : selectedExpectancy?.trailingSL

        state.slShort = useClusterExpectancy ? slExpSelected : enter1 + enter1 * slExpSelected
        state.slShort += tickSize

        state.tslShort = useClusterExpectancy ? maxTSL : state.slShort + state.slShort * tslExpSelected
        state.tslDeltaShort = maxTSLDev

        state.slsShort = []
        // console.log('sl short', levelL1, sl, sl2, state.slsShort)

        // **************************
        // TAKE PROFITS

        state.tpTargetShort = selectedPrice - selectedPrice * waveTargetPercent
        // state.tpShort = selectedPrice - selectedPrice * slPercentShort
        // state.tpsShort = [pullbackTargetDeltaLastWaveUpdated.price]
        // state.tpsShort = [
        //   enter1 - enter1 * currentExpectancy?.target1,
        //   enter1 - enter1 * currentExpectancy?.target2,
        //   // enter1 - lastWaveUpdated.deltaPercent * currentExpectancy?.targetRatio1,
        //   // enter1 - lastWaveUpdated.deltaPercent * currentExpectancy?.targetRatio2,
        //   // selectedPrice - blendedExpectancy?.target1,
        //   // selectedPrice - blendedExpectancy?.target2
        // ]
        // state.tpsShort = clusterTargetsDown.map(target => levelPrice - target)
        // if (!state.tpsShort.length) {
        //   state.tpsShort = [
        //     selectedPrice - selectedPrice * slPercentShort,
        //   ]
        // }

        if (!state.tpsShort.includes(tp1)) {
          state.tpsShort.push(tp1)
        }

        if (!state.tpsShort.includes(tp2)) {
          state.tpsShort.push(tp2)
        }

        state.tpsShort.sort((a, b) => b - a)


        const positionSizeDelta = state.balance * positionPercent * weight
        const fee = positionSizeDelta * limitFee
        const deltaGross = -positionSizeDelta
        const deltaNet = deltaGross - fee + fee * cashbackRate

        state.shortPosition += positionSizeDelta

        state.balance += deltaNet
        state.balanceShort += deltaNet

        state.feeTotal += fee
        state.feeShort += fee

        // if (!state?.enteredShort?.length) {
        //   console.log('enter short invalid enteredShort')
        // }
        // state.enteredShort.push(state.entryPointsShort[i])
        // state.entryPointsShort.splice(i, 1)

        const ind = {}
        if (state.actionsShort[state.actionsShort.length - 1]?.entries?.length) {
          state.actionsShort[state.actionsShort.length - 1]?.entries.push({
            clusterId,
            type: 'enter',
            time: candle.time,
            value: price,
            trade: {
              fee,
              deltaGross,
              deltaNet,
              positionSize: state.shortPosition,
              positionDelta: positionSizeDelta,
              avgEntryPrice,
              metadata: {
                expectancy: selectedExpectancy.netExpectancy,
              },
            }
          })
        } else {
          for (let indKey in indicators) {
            const { _, ...vals } = indicators[indKey]
            ind[indKey] = {}
            for (let key in vals) {
              if (typeof indicators[indKey][key] === 'object') {
                ind[indKey][key] = { ...vals[key] }
              } else {
                ind[indKey][key] = vals[key]
              }
            }
          }

          state.actionsShort[state.actionsShort.length - 1]?.entries.push({
            clusterId,
            type: 'enter',
            time: candle.time,
            value: price,
            selectedProps,
            // indicators: ind,
            trade: {
              lvlPrice: state.shortLvlPrice,
              levelH1: state.shortLevelH1,
              levelL1: state.shortLevelL1,
              // levelH1Rev: state.shortLevelH1Rev,
              // levelL1Rev: state.shortLevelL1Rev,
              enterPrice: state.shortEnterPrice,
              sl: state.slShort,
              sls: state.slsShort,
              tp: state.tpShort,
              tps: [...state.tpsShort],
              tpTarget: state.tpTargetShort,
              entryPointsShort: [...state.entryPointsShort],
              enteredShort: [...state.enteredShort],
              avgEntryPrice,
              pullbackTargetDeltaLastWaveUpdatedPrice: pullbackTargetDeltaLastWaveUpdated.price,
              moveTargetDeltaLastWaveUpdatedPrice: moveTargetDeltaLastWaveUpdated.price,
              pullbackTargetDeltaPrevWavePrice: pullbackTargetDeltaPrevWave.price,
              moveTargetDeltaPrevWavePrice: moveTargetDeltaPrevWave.price,
              fee,
              deltaGross,
              deltaNet,
              positionSize: state.shortPosition,
              positionDelta: positionSizeDelta,
              metadata: {
                expectancy: selectedExpectancy.netExpectancy,
              },
            }
          })

          expectancyDebug.push({
            clusterExpectancy,
            optimalWaves: optimalWaves.slice(-3),
          })
        }



        if (logEnters) {
          const stateClone = JSON.parse(JSON.stringify(state))
          console.log('enter short', state.balance, deltaNet, price, stateClone, candle)
        }
      }
    }
  }
  // }

  return drawPoints
}

export function checkEnterLongManager(reverse, candles, props, state, indicators, hasChanges, symbolInfo, backtestSettings, expectancyDebug, portfolioManager) {
  const candle = candles[candles.length - 1]
  const prevCandle = candles[candles.length - 2]
  const selectedProps = state.propsLong ? state.propsLong : props
  // const { blendedExpectancy } = props


  const {
    calc,
    optimalWaves,
    lastRealWave,
    lastWaveUpdated,
  } = selectedProps

  const {
    closestHighs, closestLows,
    closestHighsPrices, closestLowsPrices,
    closestHighsPricesRev, closestLowsPricesRev,
    //
    bounceEnterLong, bounceEnterShort,
    bounceSLPercentLong, bounceSLPercentShort,
    clusterTargetsUp, clusterTargetsDown,
    waveTargetDeltaUp, waveTargetDeltaDown,
    waveTargetPercent,
    //
    pullbackTargetDeltaPrevWave,
    moveTargetDeltaPrevWave,
    pullbackTargetDeltaLastWaveUpdated,
    moveTargetDeltaLastWaveUpdated,
    //
    predictedWave,
    predictedCurrentWave,
    expectancies,
    clusterExpectancy,
  } = calc

  if (!portfolioManager || typeof portfolioManager.balance !== 'number' || isNaN(portfolioManager.balance)) {
    console.error('КРИТИЧЕСКАЯ ОШИБКА в checkEnterLongManager: portfolioManager или его баланс некорректны!', portfolioManager);
    return; // Прерываем выполнение функции
  }

  // console.log('checkEnter', selectedProps, hasChanges)

  const prevExpectancy = expectancies?.prevLong?.blendedExpectancy

  const currentExpectancyShort = expectancies?.currentShort?.blendedExpectancy
  const currentExpectancyLong = expectancies?.currentLong?.blendedExpectancy
  const currentExpectancy = currentExpectancyLong

  const useClusterExpectancy = clusterExpectancy.currentPullbackRatio.length || clusterExpectancy.currentMoveRatio.length
  const combinedClusters = [...clusterExpectancy.currentPullbackRatio, ...clusterExpectancy.currentMoveRatio]

  // currentClusterExpectancy.sort((a, b) => a.cluster.initial.prob - b.cluster.initial.prob)
  const selectedClusterExpectancy = clusterExpectancy.currentPullbackRatio[0] // combinedClusters
  const selectedExpectancy = useClusterExpectancy ? selectedClusterExpectancy : currentExpectancy

  const filtersDefaultState = false

  const priceFilter = symbolInfo?.filters?.find(f => f.filterType === 'PRICE_FILTER')
  const tickSize = parseFloat(priceFilter?.tickSize || candle.close / 1000)

  const levelL1 = closestLowsPrices[closestLowsPrices.length - 1]
  const levelH1 = closestHighsPrices[closestHighsPrices.length - 1]
  // const levelL1Rev = closestLowsPricesRev[closestLowsPricesRev.length - 1]
  // const levelH1Rev = closestHighsPricesRev[closestHighsPricesRev.length - 1]

  const prevWave = optimalWaves[optimalWaves.length - 2]
  // const enter1 = prevWave.high - prevWave.deltaPercent * prevExpectancy?.targetRatio1
  // const enter1 = prevWave.high - prevWave.high * prevWave.deltaPercent * selectedExpectancy?.enterRatio
  const enter1 = prevWave.high - prevWave.high * selectedExpectancy?.enter
  // const enter2 = prevWave.high - prevWave.deltaPercent * prevExpectancy?.targetRatio2

  // console.log('DEBUG', enter1, enter2, prevWave.high, prevWave)
  // *****************************************************
  // LONG
  // *****************************************************
  const enterLong = bounceEnterLong?.optimal?.limit
  const slPercentLong = bounceSLPercentLong?.optimal?.limit
  const hasAllParamsLong = enterLong && slPercentLong && waveTargetDeltaUp//waveTargetDeltaDown

  state.enteredLong = state.enteredLong || []

  let longFilterAllow = filtersDefaultState

  // const target1Price = levelL1 + levelL1 * lastWaveUpdated.deltaPercent * selectedExpectancy?.targetRatio1
  const target1Price = levelL1 + levelL1 * selectedExpectancy?.target1
  const isNextTargetOk1 = target1Price / prevCandle.close > 1 + minTargetPercent
  const isNextTargetOk2 = pullbackTargetDeltaLastWaveUpdated.price / prevCandle.close > 1 + minTargetPercent
  const isNextTargetOk = true // isNextTargetOk1 // && isNextTargetOk2
  const isCurrentPriceOk = true // prevCandle.low <= pullbackTargetDeltaPrevWave.price || candle.low <= pullbackTargetDeltaPrevWave.price

  const isIgnoreOk = !(longIgnoreEnabled && state?.ignoreLong === optimalWaves.length)
  const isSequenceOk = true
    && predictedWave?.direction === 'up'
    && predictedCurrentWave?.type === 'pullback'
    && predictedWave?.type === 'move'

  const isIndicatorOK = true
  // && indicators?.['CCI_42']?.value >= 0
  // && indicators?.['ZeroLagChaikinOscillator_44_111']?.value >= 0
  // && indicators?.['ZeroLagChaikinOscillator_44_111']?.value > indicators?.['ZeroLagChaikinOscillator_66_155']?.value
  // indicators?.['CCI_42']?.value >= 0 // 'PSAR_0.002_0.007_0.2' // indicators?.['Stochastic_64_256']?.value?.k >= 25


  const isMinExpectancyOK = true // currentExpectancy?.netExpectancy > backtestSettings.minExpectancy //0.0015
  const isBestSide = true //currentExpectancyShort?.netExpectancy >= currentExpectancyLong?.netExpectancy

  const longFilter = true
    && isNextTargetOk && isCurrentPriceOk
    && isIndicatorOK && isIgnoreOk
    && isSequenceOk
    && isMinExpectancyOK && isBestSide


  if (longFilter) {
    longFilterAllow = true
  }

  // console.log('enter long check1', state.inLong, state.entryPointsLong, state.enteredLong, )
  const maxTradesReached = (state?.actionsLong?.length || 0) > backtestSettings.tradesLimit

  const longPositionCheck = !state.inLong || state.longPosition === 0 // !state.inLong // || (state.inLong && state.entryPointsLong.length)
  const allowEnterLong = backtestSettings.enableLong
    && longFilterAllow && longPositionCheck && hasAllParamsLong
    && !maxTradesReached
  // && closestLowsPrices.length && closestHighsPrices.length


  const getClusterGroupPoints = (group, groupId, groupWeight = 1) => {
    const entryPoints = []

    let minSL = Infinity
    let minTSL = Infinity
    let maxTSLDev = -Infinity

    const clusterWeights = calculateClusterWeights(group, BacktestSettings.clusterWeightsMode.value, groupWeight) // 'netExpectancy' , 'prob', etc

    for (let i = 0; i < group.length; i++) {
      const cluster = group[i]

      const enterLevel = prevWave.high - prevWave.high * cluster?.enter
      const enters = getClusterEnters({
        direction: 'long',
        clusterWeight: clusterWeights.weights[i],
        percentToLvl: cluster.enterDev, //selectedExpectancy?.stopLoss, //enterLong,
        percentToStop: cluster.enterDev, //selectedExpectancy?.stopLoss, //enterLong, //slPercentLong,
        lvlPrice: enterLevel, //levelL1, //candle.close,
      })

      const tp1 = enterLevel + enterLevel * cluster.target1
      const tp2 = enterLevel + enterLevel * cluster.target2

      const sl = enterLevel - enterLevel * cluster.stopLoss
      const tsl = enterLevel - enterLevel * cluster.trailingSL

      if (sl < minSL) minSL = sl
      if (tsl < minTSL) minTSL = tsl
      if (cluster.trailingSL > maxTSLDev) maxTSLDev = cluster.trailingSL


      entryPoints.push({
        clusterId: `${groupId}_${i}`,
        cluster,
        level: enterLevel,
        enterDev: cluster.enterDev,
        enters,
        index: i,
        total: combinedClusters.length,
        clusterWeight: clusterWeights.weights[i],
        tp1,
        tp2,
        sl,
        tsl
      })
    }

    entryPoints.forEach(point => {
      point.minSL = minSL // Math.min(minSL, minTSL)
      point.minTSL = minTSL
      point.maxTSLDev = maxTSLDev
    })

    return entryPoints
  }


  if (allowEnterLong) {  //&& !portfolioManager.activePositions.some(p => p.direction === 'long')

    let potentialTrades

    // 1. Получаем группы кластеров
    if (BacktestSettings.clusterStopMode.value === 'overall') {
      const comboClusters = [...clusterExpectancy.currentPullbackRatio, ...clusterExpectancy.currentMoveRatio]
      const comboPoints = getClusterGroupPoints(comboClusters, 'combo', 1)
      potentialTrades = [...comboPoints];
    } else {
      const cprPoints = getClusterGroupPoints(clusterExpectancy.currentPullbackRatio, 'currentPullback', 0.5) //cprWeights.total / totalWeights
      const cmrPoints = getClusterGroupPoints(clusterExpectancy.currentMoveRatio, 'currentMove', 0.5) //  cmrWeights.total / totalWeights
      potentialTrades = [...cprPoints, ...cmrPoints];
    }


    // 2. Рассчитываем размеры позиций
    const totalRiskPercent = 0.01;
    const sizedTrades = calculatePositionSizes(potentialTrades, totalRiskPercent, portfolioManager.balance);

    // 3. Создаем и добавляем Position для КАЖДОЙ возможности
    for (const tradeData of sizedTrades) {
      const positionId = `wave_${optimalWaves.length}_cluster_${tradeData.clusterId}_${tradeData.direction}`;
      const isDuplicate = portfolioManager.activePositions
        .concat(portfolioManager.pendingPositions)
        .some(p => p.id === positionId);
      if (isDuplicate) continue;

      if (!tradeData.size || tradeData.size <= 0) {
        console.warn(`Пропуск создания позиции для #${tradeData.clusterId}, так как размер равен 0.`);
        continue; // Переходим к следующему кластеру
      }

      if (tradeData.cluster.netExpectancy <= 0) {
        console.warn(`Пропуск создания позиции для #${tradeData.clusterId}, так как netExpectancy меньше 0.`);
        continue; // Переходим к следующему кластеру
      }

      const entryOrders = tradeData.enters.map(e => ({
        price: e.price,
        size: e.weight * tradeData.size // size от RiskManager'а
      }));

      const isSoloSL = BacktestSettings.clusterStopMode.value === 'solo'

      const positionParams = {
        id: positionId,
        direction: 'long',
        creationWaveCount: optimalWaves.length,
        entryOrders,
        stopLoss: isSoloSL ? tradeData.sl : tradeData.minSL, //tradeData.minSL,
        tslDistance: isSoloSL ? tradeData.tsl : tradeData.maxTSLDev, //tradeData.maxTSLDev, // Используем maxTSLDev как дистанцию
        takeProfits: [tradeData.tp1, tradeData.tp2],
        partialClosePercent: backtestSettings.takePercent,
        originId: `cluster_${tradeData.clusterId}`,
        creationTime: candle.time,
        metadata: {
          expectancy: tradeData.cluster.netExpectancy,
          probability: tradeData.cluster.prob,
          //
          waves: optimalWaves.length,
          pullbackTargetDeltaPrevWavePrice: calc.pullbackTargetDeltaPrevWave?.price,
          moveTargetDeltaPrevWavePrice: calc.moveTargetDeltaPrevWave?.price,
          pullbackTargetDeltaLastWaveUpdatedPrice: calc.pullbackTargetDeltaLastWaveUpdated?.price,
          moveTargetDeltaLastWaveUpdatedPrice: calc.moveTargetDeltaLastWaveUpdated?.price
        }
      };

      const newPosition = new Position(positionParams);
      portfolioManager.addPosition(newPosition);

      expectancyDebug.push({
        clusterExpectancy,
        optimalWaves: optimalWaves.slice(-3),
      })
    }
  }
}

export function checkEnterShortManager(reverse, candles, props, state, indicators, hasChanges, symbolInfo, backtestSettings, expectancyDebug, portfolioManager) {
  const candle = candles[candles.length - 1]
  const prevCandle = candles[candles.length - 2]
  const selectedProps = state.propsShort ? state.propsShort : props
  const {
    calc,
    optimalWaves,
    lastRealWave,
    lastWaveUpdated,
  } = selectedProps

  const {
    closestHighs, closestLows,
    closestHighsPrices, closestLowsPrices,
    closestHighsPricesRev, closestLowsPricesRev,
    //
    bounceEnterLong, bounceEnterShort,
    bounceSLPercentLong, bounceSLPercentShort,
    clusterTargetsUp, clusterTargetsDown,
    waveTargetDeltaUp, waveTargetDeltaDown,
    waveTargetPercent,
    //
    pullbackTargetDeltaPrevWave,
    moveTargetDeltaPrevWave,
    pullbackTargetDeltaLastWaveUpdated,
    moveTargetDeltaLastWaveUpdated,
    //
    predictedWave,
    predictedCurrentWave,
    expectancies,
    clusterExpectancy,

  } = calc

  if (!portfolioManager || typeof portfolioManager.balance !== 'number' || isNaN(portfolioManager.balance)) {
    console.error('КРИТИЧЕСКАЯ ОШИБКА в checkEnterShortManager: portfolioManager или его баланс некорректны!', portfolioManager);
    return; // Прерываем выполнение функции
  }

  const prevExpectancy = expectancies?.prevShort?.blendedExpectancy

  const currentExpectancyShort = expectancies?.currentShort?.blendedExpectancy
  const currentExpectancyLong = expectancies?.currentLong?.blendedExpectancy
  const currentExpectancy = currentExpectancyShort

  const useClusterExpectancy = clusterExpectancy.currentPullbackRatio.length || clusterExpectancy.currentMoveRatio.length
  const combinedClusters = [...clusterExpectancy.currentPullbackRatio, ...clusterExpectancy.currentMoveRatio]

  // currentClusterExpectancy.sort((a, b) => a.cluster.initial.prob - b.cluster.initial.prob)
  const selectedClusterExpectancy = combinedClusters[0]
  const selectedExpectancy = useClusterExpectancy ? selectedClusterExpectancy : currentExpectancy


  const filtersDefaultState = false

  const priceFilter = symbolInfo?.filters?.find(f => f.filterType === 'PRICE_FILTER')
  const tickSize = parseFloat(priceFilter?.tickSize || candle.close / 1000)


  const levelL1 = closestLowsPrices[closestLowsPrices.length - 1]
  const levelH1 = closestHighsPrices[closestHighsPrices.length - 1]
  // const levelL1Rev = closestLowsPricesRev[closestLowsPricesRev.length - 1]
  // const levelH1Rev = closestHighsPricesRev[closestHighsPricesRev.length - 1]

  const prevWave = optimalWaves[optimalWaves.length - 2]
  // const enter1 = prevWave.high - prevWave.deltaPercent * prevExpectancy?.targetRatio1
  // const enter1 = prevWave.high - prevWave.high * prevWave.deltaPercent * currentExpectancy?.enterRatio
  const enter1 = prevWave.low + prevWave.low * currentExpectancy?.enter
  // const enter2 = prevWave.high - prevWave.deltaPercent * prevExpectancy?.targetRatio2
  // *****************************************************
  // SHORT
  // *****************************************************
  const enterShort = bounceEnterShort?.optimal?.limit
  const slPercentShort = bounceSLPercentShort?.optimal?.limit
  const hasAllParamsShort = enterShort && slPercentShort && waveTargetDeltaDown

  state.enteredShort = state.enteredShort || []

  let shortFilterAllow = filtersDefaultState

  // const target1Price = levelL1 + levelL1 * lastWaveUpdated.deltaPercent * currentExpectancy?.targetRatio1
  const target1Price = levelH1 - levelH1 * currentExpectancy?.target1
  const isNextTargetOk1 = prevCandle.close / target1Price > 1 + minTargetPercent
  const isNextTargetOk2 = prevCandle.close / pullbackTargetDeltaLastWaveUpdated.price > 1 + minTargetPercent
  const isNextTargetOk = true // isNextTargetOk1 // && isNextTargetOk2

  const isCurrentPriceOk = true // prevCandle.high >= pullbackTargetDeltaPrevWave.price || candle.high >= pullbackTargetDeltaPrevWave.price
  const isIgnoreOk = !(shortIgnoreEnabled && state?.ignoreShort === optimalWaves.length)
  const isSequenceOk = true
    && predictedWave?.direction === 'down'
    && predictedCurrentWave?.type === 'pullback'
    && predictedWave?.type === 'move'

  const isIndicatorOK = true
  // && indicators["Stochastic_42_144"]?.value?.k > 80
  // && indicators?.['CCI_42']?.value <= 0 
  // && indicators?.['CCI_42']?.value >= 0 
  // && indicators?.['Stochastic_64_256']?.value?.k >= 25
  // && indicators["RSI_avg4_42"]?.value < 40

  const isMinExpectancyOK = true //currentExpectancy?.netExpectancy > backtestSettings.minExpectancy //0.0015
  const isBestSide = true //currentExpectancyShort?.netExpectancy >= currentExpectancyLong?.netExpectancy

  const shortFilter = true
    && isNextTargetOk && isCurrentPriceOk
    && isIndicatorOK && isIgnoreOk
    && isSequenceOk
    && isMinExpectancyOK && isBestSide

  if (shortFilter) {
    shortFilterAllow = true
  }

  const maxTradesReached = Math.floor((state?.actionsShort?.length || 0) / 2) > backtestSettings.tradesLimit

  const shortPositionCheck = !state.inShort || state.shortPosition === 0  //|| (state.inShort && state.entryPointsShort.length)
  const allowEnterShort = backtestSettings.enableShort
    && shortFilterAllow && shortPositionCheck && hasAllParamsShort
    && closestLowsPrices.length && closestHighsPrices.length
    && !maxTradesReached

  const getClusterGroupPointsShort = (group, groupId, groupWeight = 1) => {
    const entryPoints = []

    let maxSL = -Infinity
    let maxTSL = -Infinity
    let maxTSLDev = -Infinity

    const clusterWeights = calculateClusterWeights(group, BacktestSettings.clusterWeightsMode.value, groupWeight) // 'netExpectancy' , 'prob', etc

    for (let i = 0; i < group.length; i++) {
      const cluster = group[i]

      const enterLevel = prevWave.low + prevWave.low * cluster?.enter
      const enters = getClusterEnters({
        direction: 'short',
        clusterWeight: clusterWeights.weights[i],
        percentToLvl: cluster.enterDev, //selectedExpectancy?.stopLoss, //enterLong,
        percentToStop: cluster.enterDev, //selectedExpectancy?.stopLoss, //enterLong, //slPercentLong,
        lvlPrice: enterLevel, //levelL1, //candle.close,
      })

      const tp1 = enterLevel - enterLevel * cluster.target1
      const tp2 = enterLevel - enterLevel * cluster.target2

      const sl = enterLevel + enterLevel * cluster.stopLoss
      const tsl = enterLevel + enterLevel * cluster.trailingSL

      if (sl > maxSL) maxSL = sl
      if (tsl > maxTSL) maxTSL = tsl
      if (cluster.trailingSL > maxTSLDev) maxTSLDev = cluster.trailingSL


      entryPoints.push({
        clusterId: `${groupId}_${i}`,
        cluster,
        level: enterLevel,
        enterDev: cluster.enterDev,
        enters,
        index: i,
        total: combinedClusters.length,
        clusterWeight: clusterWeights.weights[i],
        tp1,
        tp2,
        sl,
        tsl
      })
    }

    entryPoints.forEach(point => {
      point.maxSL = maxSL // Math.min(maxSL, maxTSL)
      point.maxTSL = maxTSL
      point.maxTSLDev = maxTSLDev
    })

    return entryPoints
  }


  // const hasShorts = portfolioManager.activePositions.some(p => p.direction === 'short')
  if (allowEnterShort) { // && !hasShorts
    let potentialTrades

    // 1. Получаем группы кластеров
    if (BacktestSettings.clusterStopMode.value === 'overall') {
      const comboClusters = [...clusterExpectancy.currentPullbackRatio, ...clusterExpectancy.currentMoveRatio]
      const comboPoints = getClusterGroupPointsShort(comboClusters, 'combo', 1)
      potentialTrades = [...comboPoints];
    } else {
      const cprPoints = getClusterGroupPointsShort(clusterExpectancy.currentPullbackRatio, 'currentPullback', 0.5) //cprWeights.total / totalWeights
      const cmrPoints = getClusterGroupPointsShort(clusterExpectancy.currentMoveRatio, 'currentMove', 0.5) //  cmrWeights.total / totalWeights
      potentialTrades = [...cprPoints, ...cmrPoints];
    }

    // 2. Рассчитываем размеры позиций
    const totalRiskPercent = 0.01;
    const sizedTrades = calculatePositionSizes(potentialTrades, totalRiskPercent, portfolioManager.balance);

    // 3. Создаем и добавляем Position для КАЖДОЙ возможности
    for (const tradeData of sizedTrades) {
      const positionId = `wave_${optimalWaves.length}_cluster_${tradeData.clusterId}_${tradeData.direction}`;

      const isDuplicate = portfolioManager.activePositions
        .concat(portfolioManager.pendingPositions)
        .some(p => p.id === positionId);
      if (isDuplicate) continue;

      if (!tradeData.size || tradeData.size <= 0) {
        console.warn(`Пропуск создания позиции для #${tradeData.clusterId}, так как размер равен 0.`);
        continue; // Переходим к следующему кластеру
      }

      if (tradeData.cluster.netExpectancy <= 0) {
        console.warn(`Пропуск создания позиции для #${tradeData.clusterId}, так как netExpectancy меньше 0.`);
        continue; // Переходим к следующему кластеру
      }

      const entryOrders = tradeData.enters.map(e => ({
        price: e.price,
        size: e.weight * tradeData.size // size от RiskManager'а
      }));

      const isSoloSL = BacktestSettings.clusterStopMode.value === 'solo'

      const positionParams = {
        id: positionId,
        direction: 'short',
        creationWaveCount: optimalWaves.length,
        entryOrders,
        stopLoss: isSoloSL ? tradeData.sl : tradeData.maxSL, // Для шорта используем maxSL
        tslDistance: isSoloSL ? tradeData.tsl : tradeData.maxTSLDev,
        takeProfits: [tradeData.tp1, tradeData.tp2],
        partialClosePercent: backtestSettings.takePercent,
        originId: `cluster_${tradeData.clusterId}`,
        creationTime: candle.time,
        metadata: {
          expectancy: tradeData.cluster.netExpectancy,
          probability: tradeData.cluster.prob,
          // 
          waves: optimalWaves.length,
          pullbackTargetDeltaPrevWavePrice: calc.pullbackTargetDeltaPrevWave?.price,
          moveTargetDeltaPrevWavePrice: calc.moveTargetDeltaPrevWave?.price,
          pullbackTargetDeltaLastWaveUpdatedPrice: calc.pullbackTargetDeltaLastWaveUpdated?.price,
          moveTargetDeltaLastWaveUpdatedPrice: calc.moveTargetDeltaLastWaveUpdated?.price
        }
      };

      const newPosition = new Position(positionParams);
      portfolioManager.addPosition(newPosition);

      expectancyDebug.push({
        clusterExpectancy,
        optimalWaves: optimalWaves.slice(-3),
      })
    }
  }
}

// ****************************
// EXITS
// ****************************

export function checkExitLong(candles, props, state, indicators, hasChanges, symbolInfo, backtestSettings) {
  const candle = candles[candles.length - 1]
  const prevCandle = candles[candles.length - 2]
  const selectedProps = state.propsLong ? state.propsLong : props
  const {
    closestHighs, closestLows,
    bounceEnterLong, bounceEnterShort,
    bounceSLPercentLong, bounceSLPercentShort,
    clusterTargetsUp, clusterTargetsDown,
    waveTargetDeltaUp, waveTargetDeltaDown,
    waveTargetPercent,
    pullbackRatio,
    currentCategory,
    calc,
  } = selectedProps

  const { expectancies, clusterExpectancy } = calc

  const { inLong, slLong, tslLong, tpLong, tpsLong, } = state
  const blendedExpectancy = expectancies?.currentLong?.blendedExpectancy


  const drawPoints = []

  if (state.inLong) {
    const avgEntrySumm = state.enteredLong.reduce((acc, entry) => acc + entry.price * entry.weight, 0)
    const totalWeight = state.enteredLong.reduce((acc, entry) => acc + entry.weight, 0)
    const avgEntryPrice = avgEntrySumm / totalWeight

    const pnl = (candle.close - avgEntryPrice) * state.longPosition
    state.pnl = pnl

    // *********************************
    // STOP LOSS
    // *********************************

    if (slLong && state.inLong) {
      if ((candle.low <= slLong || prevCandle.low <= slLong) && (candle.high >= slLong || prevCandle.high >= slLong)) {
        onLongSL(state, candle, slLong, avgEntryPrice, 'SL', drawPoints)
        return null
      }
      if ((candle.low <= tslLong || prevCandle.low <= tslLong) && (candle.high >= tslLong || prevCandle.high >= tslLong)) {
        onLongSL(state, candle, tslLong, avgEntryPrice, 'TSL', drawPoints)
        return null
      }
    }

    // *********************************
    // TAKE PROFITS
    // *********************************

    if (tpsLong && state.inLong) {
      // console.log('exit long check', candle.high > enter, candle.high, enter, level, inLong, enterLong, closestHighs.length)
      for (let i = 0; i < tpsLong.length; i++) {
        if (candle.high >= tpsLong[i]) {
          const priceTP = tpsLong.shift()

          if (!tpsLong.length) {
            tpsLong.push(priceTP * (1 + backtestSettings.nextTakeDeltaPercent))
          }
          const price = Math.max(priceTP) //, candle.high) // TODO: remove
          const deltaPercent = (price - avgEntryPrice) / avgEntryPrice
          let selectedTake = backtestSettings.takePercent


          if (state.actionsLong[state.actionsLong.length - 1]?.exits?.length > backtestSettings.takesLimit) {
            selectedTake = 1
          }


          const amount = state.longPosition * selectedTake
          const fee = amount * limitFee

          const deltaGross = amount * (1 + deltaPercent)
          const deltaNet = deltaGross - fee + fee * cashbackRate

          state.balance += deltaNet
          state.balanceLong += deltaNet

          state.longPosition *= (1 - selectedTake)


          const lastPOI = state.triggeredTPsLong.length
            ? state.triggeredTPsLong[state.triggeredTPsLong.length - 1]
            : avgEntryPrice
          const newSL = lastPOI + (priceTP - lastPOI) * 0.3 //0.75

          const zeroProfitPrice = avgEntryPrice * (1 + zeroProfitPercent)
          state.slLong = Math.max(zeroProfitPrice, newSL)

          state.triggeredTPsLong.push(priceTP)

          const point = {
            time: candle.time,
            value: price,
            closed: false,
            type: 'TP',
          }

          if (!tpsLong.length || !state.longPosition) {
            point.closed = true
            state.inLong = false
            state.propsLong = null
            state.longExitNumber = state.longExitNumber ? state.longExitNumber + 1 : 1
            state.enteredLong = []
            state.entryPointsLong = []
            state.longPosition = 0

            // if (deltaNet > state.longPosition) {
            //   state.longWins += 1
            //   state.longWin += deltaNet
            // } else {
            //   state.longLosses += 1
            //   state.longLoss += deltaNet
            // }
            // state.longPosition = 0
          } else {
            // state.longPosition -= deltaNet
          }


          const trade = {
            lvlPrice: state.longLvlPrice,
            enterPrice: state.longEnterPrice,
            sl: state.slLong,
            tp: state.tpLong,
            tps: [...state.tpsLong],
            tpTarget: state.tpTargetLong,
            entryPointsLong: [...state.entryPointsLong],
            enteredLong: [...state.enteredLong],
            deltaPercent,
            fee,
            deltaGross,
            deltaNet,
            positionSize: state.longPosition,
          }

          state.actionsLong[state.actionsLong.length - 1].exits.push({
            type: 'exit',
            reason: 'TP',
            time: candle.time,
            value: price,
            newSL: state.slLong,
            candle,
            selectedProps,
            props,
            trade,
          })

          drawPoints.push(point)
          if (logExits) console.log('exit long TP', state.balance, deltaNet, price, avgEntryPrice, state.longLvlPrice, state.inLong, tpsLong.length, candle)
        }
      }
    }

    // *********************************
    // UPDATE TRALING SL
    // *********************************
    // const etsl = blendedExpectancy.trailingSL //
    // const etsl = state.triggeredTPsLong?.length ? blendedExpectancy.smallTSL : blendedExpectancy.bigTSL
    const etsl = state.tslDeltaLong
    if (etsl && candle.high - candle.high * etsl > state.tslLong) {
      state.tslLong = candle.high - candle.high * etsl
    }

  }
  else {
    state.pnl = 0
  }

  return drawPoints
}

export function checkExitShort(candles, props, state, indicators, hasChanges, symbolInfo, backtestSettings) {
  const candle = candles[candles.length - 1]
  const prevCandle = candles[candles.length - 2]
  const selectedProps = state.propsShort ? state.propsShort : props
  const {
    closestHighs, closestLows,
    bounceEnterLong, bounceEnterShort,
    bounceSLPercentLong, bounceSLPercentShort,
    clusterTargetsUp, clusterTargetsDown,
    waveTargetDeltaUp, waveTargetDeltaDown,
    waveTargetPercent,
    pullbackRatio,
    currentCategory,
    calc,
  } = selectedProps
  const { expectancies, clusterExpectancy } = calc

  let { inShort, shortEnterPrice, slShort, tslShort, tpsShort } = state
  const blendedExpectancy = expectancies?.currentShort?.blendedExpectancy

  const drawPoints = []

  if (state.inShort) {
    const avgEntrySumm = state.enteredShort.reduce((acc, entry) => acc + entry.price * entry.weight, 0)
    const totalWeight = state.enteredShort.reduce((acc, entry) => acc + entry.weight, 0)
    const avgEntryPrice = avgEntrySumm / totalWeight

    const pnl = (avgEntryPrice - candle.close) * state.shortPosition
    state.pnl = pnl

    // *********************************
    // STOP LOSS
    // *********************************

    if (slShort && state.inShort) {
      //  if ((candle.low <= slLong || prevCandle.low <= slLong) && (candle.high >= slLong || prevCandle.high >= slLong)) {

      // console.log('exit long check', candle.high > enter, candle.high, enter, level, inLong, enterLong, closestHighs.length)
      if ((candle.high >= slShort || prevCandle.high >= slShort) && (candle.low <= slShort || prevCandle.low <= slShort)) {
        onShortSL(state, candle, slShort, avgEntryPrice, 'SL', drawPoints)
        return null
      }
      if ((candle.high >= tslShort || prevCandle.high >= tslShort) && (candle.low <= tslShort || prevCandle.low <= tslShort)) {
        onShortSL(state, candle, tslShort, avgEntryPrice, 'TSL', drawPoints)
        return null
      }
    }

    // *********************************
    // TAKE PROFITS
    // *********************************

    if (tpsShort && state.inShort) {
      // console.log('exit long check', candle.high > enter, candle.high, enter, level, inLong, enterLong, closestHighs.length)
      for (let i = 0; i < tpsShort.length; i++) {
        if (candle.low < tpsShort[i]) {
          const priceTP = tpsShort.shift()

          if (!tpsShort.length) {
            tpsShort.push(priceTP * (1 - backtestSettings.nextTakeDeltaPercent))
          }

          const price = Math.min(priceTP) //, candle.low) // TODO: remove
          const deltaPercent = (avgEntryPrice - price) / avgEntryPrice
          let selectedTake = backtestSettings.takePercent
          if (state.actionsShort[state.actionsShort.length - 1]?.exits?.length > backtestSettings.takesLimit) {
            selectedTake = 1
          }

          const amount = state.shortPosition * selectedTake
          const fee = amount * limitFee

          const deltaGross = amount * (1 + deltaPercent)
          const deltaNet = deltaGross - fee + fee * cashbackRate

          state.balance += deltaNet
          state.balanceShort += deltaNet

          state.shortPosition *= (1 - selectedTake)

          const lastPOI = state.triggeredTPsShort.length
            ? state.triggeredTPsShort[state.triggeredTPsShort.length - 1]
            : avgEntryPrice
          const newSL = lastPOI - (priceTP - lastPOI) * 0.3 //0.75

          const zeroProfitPrice = avgEntryPrice * (1 - zeroProfitPercent)
          state.slShort = Math.min(zeroProfitPrice, newSL)


          state.triggeredTPsShort.push(priceTP)

          const point = {
            time: candle.time,
            value: price,
            closed: false,
            type: 'TP',
          }

          if (!tpsShort.length || !state.shortPosition) {
            point.closed = true
            state.inShort = false
            state.propsShort = null
            state.shortExitNumber = state.shortExitNumber ? state.shortExitNumber + 1 : 1
            state.enteredShort = []
            state.entryPointsShort = []
            state.shortPosition = 0
          }

          const trade = {
            lvlPrice: state.shortLvlPrice,
            enterPrice: state.shortEnterPrice,
            sl: state.slShort,
            tsl: state.tslShort,
            tp: state.tpShort,
            tps: [...state.tpsShort],
            tpTarget: state.tpTargetShort,
            entryPointsShort: [...state.entryPointsShort],
            enteredShort: [...state.enteredShort],
            deltaPercent,
            fee,
            deltaGross,
            deltaNet,
            positionSize: state.shortPosition,
          }

          state.actionsShort[state.actionsShort.length - 1].exits.push({
            type: 'exit',
            reason: 'TP',
            time: candle.time,
            value: price,
            newSL: state.slShort,
            tsl: state.tslShort,
            candle,
            selectedProps,
            props,
            trade,
          })


          drawPoints.push(point)
          if (logExits) console.log('exit short TP', price, avgEntryPrice, state.shortLvlPrice, state.inShort, tpsShort.length, state.balance)
        }
      }
    }

    // *********************************
    // UPDATE TRALING SL
    // *********************************
    // const etsl = blendedExpectancy.trailingSL
    // const etsl = state.triggeredTPsShort?.length ? blendedExpectancy.smallTSL : blendedExpectancy.bigTSL
    const etsl = state.tslDeltaShort
    if (etsl && candle.low + candle.low * etsl > state.tslShort) {
      state.tslShort = candle.low + candle.low * etsl
    }

  }
  else {
    state.pnl = 0
  }

  return drawPoints
}

function onLongSL(state, candle, slLong, avgEntryPrice, reason, drawPoints) {
  // console.log('exit long check', candle.high > enter, candle.high, enter, level, inLong, enterLong, closestHighs.length)
  const deltaPercent = (slLong - avgEntryPrice) / avgEntryPrice
  const fee = state.longPosition * marketFee

  const deltaGross = state.longPosition * (1 + deltaPercent)
  const deltaNet = deltaGross - fee + fee * cashbackRate

  // console.log('SL', state.balance, deltaNet, deltaGross, fee, deltaPercent)

  // if (deltaNet > state.longPosition + fee) {
  //   state.longWins += 1
  //   state.longWin += deltaNet
  // } else {
  //   state.longLosses += 1
  //   state.longLoss += deltaNet
  // }
  state.longPosition = 0

  state.balance += deltaNet
  state.balanceLong += deltaNet

  const trade = {
    lvlPrice: state.longLvlPrice,
    enterPrice: state.longEnterPrice,
    sl: state.slLong,
    tsl: state.tslLong,
    tp: state.tpLong,
    tps: [...state.tpsLong],
    tpTarget: state.tpTargetLong,
    entryPointsLong: [...state.entryPointsLong],
    enteredLong: [...state.enteredLong],
    deltaPercent,
    fee,
    deltaGross,
    deltaNet,
  }

  state.inLong = false
  state.propsLong = null
  state.longExitNumber = state.longExitNumber ? state.longExitNumber + 1 : 1
  state.enteredLong = []
  state.entryPointsLong = []
  // state.positionSize = 0

  if (logExits) console.log('exit long SL', state.balance, deltaNet, slLong, avgEntryPrice, state.longLvlPrice, candle)
  drawPoints.push({
    time: candle.time,
    value: slLong,
    closed: true,
    type: 'SL',
  })

  state.actionsLong[state.actionsLong.length - 1].exits.push({
    type: 'exit',
    reason: reason || 'SL',
    time: candle.time,
    value: slLong,
    trade,
  })

}

function onShortSL(state, candle, slShort, avgEntryPrice, reason, drawPoints) {
  const deltaPercent = (avgEntryPrice - slShort) / avgEntryPrice
  const fee = state.shortPosition * marketFee

  const deltaGross = state.shortPosition * (1 + deltaPercent)
  const deltaNet = deltaGross - fee + fee * cashbackRate

  state.shortPosition = 0

  state.balance += deltaNet
  state.balanceShort += deltaNet

  const trade = {
    lvlPrice: state.shortLvlPrice,
    enterPrice: state.shortEnterPrice,
    sl: state.slShort,
    tsl: state.tslShort,
    tp: state.tpShort,
    tps: [...state.tpsShort],
    tpTarget: state.tpTargetShort,
    entryPointsShort: [...state.entryPointsShort],
    enteredShort: [...state.enteredShort],
    deltaPercent,
    fee,
    deltaGross,
    deltaNet,
  }

  state.inShort = false
  state.propsShort = null
  state.shortExitNumber = state.shortExitNumber ? state.shortExitNumber + 1 : 1
  state.enteredShort = []
  state.entryPointsShort = []
  // state.positionSize = 0

  if (logExits) console.log('exit short SL', slShort, avgEntryPrice, state.shortLvlPrice, state.balance)
  drawPoints.push({
    time: candle.time,
    value: slShort,
    closed: true,
    type: 'SL',
  })


  state.actionsShort[state.actionsShort.length - 1].exits.push({
    type: 'exit',
    reason: reason || 'SL',
    time: candle.time,
    value: slShort,
    trade,
  })

}

// ****************************
// OTHER
// ****************************

export function getClusterEnters(opts) {
  const {
    direction,
    clusterWeight,
    percentToLvl,
    percentToStop,
    lvlPrice,
  } = opts


  let preEnterPoints = bouncePreLvlEnterPoints
  let postEnterPoints = bouncePostLvlEnterPoints

  const totalPoints = preEnterPoints + postEnterPoints

  const preShift = Math.min(percentToLvl, percentToStop)
  const preStepSize = preShift / preEnterPoints
  const postStepSize = percentToStop / (postEnterPoints + 1)

  // const priceWeights = getPriceWeights(preEnterPoints + postEnterPoints)

  const enters = []
  let startPrice = direction === 'long'
    ? lvlPrice - lvlPrice * preStepSize
    : lvlPrice + lvlPrice * preStepSize

  for (let i = 0; i < preStepSize; i++) {
    const price = direction === 'long'
      ? startPrice - lvlPrice * preStepSize * i
      : startPrice + lvlPrice * preStepSize * i

    const priceFixed = price // fixPrice(price, ss.symbolInfo, bounceRoundType)
    enters.push({
      price: parseFloat(priceFixed),
      weight: clusterWeight * 1 / totalPoints // priceWeights[bouncePreEnterPoints - i - 1]
    })
  }
  enters.reverse()

  for (let i = 1; i <= postStepSize; i++) {
    const price = direction === 'long'
      ? startPrice + lvlPrice * postStepSize * i
      : startPrice - lvlPrice * postStepSize * i

    const priceFixed = price // fixPrice(price, ss.symbolInfo, bounceRoundType)
    enters.push({
      price: parseFloat(priceFixed),
      weight: clusterWeight * 1 / totalPoints // priceWeights[bouncePreEnterPoints + i - 1]
    })
  }

  if (direction !== 'long') {
    enters.reverse()
  }

  return enters
}

function getIndicatorValues(indicators) {
  const ind = {}
  for (let indKey in indicators) {
    const { _, ...vals } = indicators[indKey]
    ind[indKey] = {}
    for (let key in vals) {
      if (typeof indicators[indKey][key] === 'object') {
        ind[indKey][key] = { ...vals[key] }
      } else {
        ind[indKey][key] = vals[key]
      }
    }
  }
  return ind
}

function wavePredictionsStats(props) {
  const {
    optimalSubset,
    predictedCurrentWaveData,
    predictionDetails,
  } = props

  // Wave Type Prediction Stats

  // console.log('wave prediction', predictedCurrentWaveData?.type, optimalSubset[optimalSubset.length-1]?.type, lastWave?.type)
  if (optimalSubset[optimalSubset.length - 1]?.type !== predictedCurrentWaveData?.type) {
    predictionDetails.totalPredictions++
    predictionDetails[predictedCurrentWaveData?.type] = predictionDetails[predictedCurrentWaveData?.type] || {
      total: 0,
      correct: 0,
    }
    predictionDetails[predictedCurrentWaveData?.type].total++
    // console.log('predictedCurrentWave', correctPredictions, totalPredictions,  predictedCurrentWaveData, lastWave, lastWaveUpdated)
  } else {
    predictionDetails.correctPredictions++
    predictionDetails.totalPredictions++
    predictionDetails[predictedCurrentWaveData?.type] = predictionDetails[predictedCurrentWaveData?.type] || {
      total: 0,
      correct: 0,
    }
    predictionDetails[predictedCurrentWaveData?.type].total++
    predictionDetails[predictedCurrentWaveData?.type].correct++
  }

  if (optimalSubset[optimalSubset.length - 1]?.type === 'pullback') {
    predictionDetails.totalPullback++
  } else if (optimalSubset[optimalSubset.length - 1]?.type === 'move') {
    predictionDetails.totalMove++
  }

  // combos
  const comboKey1 = `_${predictedCurrentWaveData?.type}_${predictedCurrentWaveData?.direction}`
  const comboKey2 = `_${optimalSubset[optimalSubset.length - 1]?.type}_${optimalSubset[optimalSubset.length - 1]?.direction}`

  if (optimalSubset[optimalSubset.length - 1]?.type !== predictedCurrentWaveData?.type
    || optimalSubset[optimalSubset.length - 1]?.direction !== predictedCurrentWaveData?.direction) {
    predictionDetails[comboKey1] = predictionDetails[comboKey1] || {
      total: 0,
      correct: 0,
    }
    predictionDetails[comboKey1].total++
  } else {
    predictionDetails[comboKey1] = predictionDetails[comboKey1] || {
      total: 0,
      correct: 0,
    }
    predictionDetails[comboKey1].total++
    predictionDetails[comboKey1].correct++
  }
}

export function getBounceEnters(opts) {
  const {
    direction,
    percentToLvl,
    percentToStop,
    lvlPrice,
  } = opts


  let bouncePreEnterPoints = bouncePreLvlEnterPoints
  let bouncePostEnterPoints = bouncePostLvlEnterPoints

  const totalPoints = bouncePreEnterPoints + bouncePostEnterPoints

  const preShift = Math.min(percentToLvl, percentToStop)
  const bouncePreStepSize = preShift / bouncePreEnterPoints
  const bouncePostStepSize = percentToStop / (bouncePostEnterPoints + 1)

  // const bouncePriceWeights = getPriceWeights(bouncePreEnterPoints + bouncePostEnterPoints)

  const bounceEnters = []
  let bounceStartPrice = direction === 'long'
    ? lvlPrice - lvlPrice * bouncePreStepSize
    : lvlPrice + lvlPrice * bouncePreStepSize

  for (let i = 0; i < bouncePreEnterPoints; i++) {
    const price = direction === 'long'
      ? bounceStartPrice - lvlPrice * bouncePreStepSize * i
      : bounceStartPrice + lvlPrice * bouncePreStepSize * i

    const priceFixed = price // fixPrice(price, ss.symbolInfo, bounceRoundType)
    bounceEnters.push({
      price: parseFloat(priceFixed),
      weight: 1 / totalPoints // bouncePriceWeights[bouncePreEnterPoints - i - 1]
    })
  }
  bounceEnters.reverse()

  for (let i = 1; i <= bouncePostEnterPoints; i++) {
    const price = direction === 'long'
      ? bounceStartPrice + lvlPrice * bouncePostStepSize * i
      : bounceStartPrice - lvlPrice * bouncePostStepSize * i

    const priceFixed = price // fixPrice(price, ss.symbolInfo, bounceRoundType)
    bounceEnters.push({
      price: parseFloat(priceFixed),
      weight: 1 / totalPoints // bouncePriceWeights[bouncePreEnterPoints + i - 1]
    })
  }

  if (direction !== 'long') {
    bounceEnters.reverse()
  }

  return bounceEnters
}

function initIndicators(indicators, buffer) {
  for (const params of indicatorsList) {
    try {
      const { key, indicator, indicatorOpts, argsValue, argsMoment } = params
      if (!indicators[key]) {
        const instance = new indicator(...indicatorOpts)
        indicators[key] = indicators[key] || {
          _: instance,
          value: null,
          moment: null,
        }

        // const candleBuffer = buffer.slice(0, (period + 1))
        // for (const candle of candleBuffer) {
        //   const { o, h, l, c, v } = candle
        //   const avg4 = (o + h + l + c) / 4

        //   const indicatorParams = {
        //     high: h,
        //     low: l,
        //     close: c,
        //     open: o,
        //     volume: v,
        //     avg4,
        //   }

        //   const paramsValue = argsValue.map(arg => indicatorParams[arg])
        //   indicators[key].value = indicators[key]._.nextValue(...paramsValue)

        //   const paramsMoment = argsMoment.map(arg => indicatorParams[arg])
        //   indicators[key].moment = indicators[key]._.momentValue(...paramsMoment)
        // }
      }
    } catch (error) {
      console.log('initIndicators error', params, error)
    }
  }

  indicators.inititated = true
}

function calculateIndicators(indicators, candles) {
  for (const candle of candles) {
    // const { o, h, l, c, v } = candle
    // const avg4 = (o + h + l + c) / 4
    // const indicatorParams = {
    //   high: h,
    //   low: l,
    //   close: c,
    //   open: o,
    //   volume: v,
    //   avg4,
    // }

    const { time, open, high, low, close, volume } = candle
    const avg4 = (open + high + low + close) / 4
    const hlc3 = (high + low + close) / 3

    const indicatorParams = {
      high,
      low,
      close,
      open,
      volume,
      avg4,
      hlc3,
    }



    const combined = {
      candle,
    }

    for (const params of indicatorsList) {
      try {
        const { key, indicator, period, argsValue, argsMoment } = params

        const paramsValue = argsValue.map(arg => indicatorParams[arg])
        indicators[key].value = indicators[key]._.nextValue(...paramsValue)

        const paramsMoment = argsMoment.map(arg => indicatorParams[arg])
        indicators[key].moment = indicators[key]._.momentValue(...paramsMoment)

        combined[key] = {
          value: indicators[key].value,
          moment: indicators[key].moment,
        }
      } catch (error) {
        console.log('calculateIndicators error', params, error)
      }
    }

    return indicators
  }
}


export function fixPrice(initPrice, symbolInfo, roundType = 'round') {
  const priceFilter = symbolInfo.filters.find(f => f.filterType === 'PRICE_FILTER')
  const tickSize = parseFloat(priceFilter.tickSize)
  const minPrice = parseFloat(priceFilter.minPrice)
  const price = Math[roundType]((initPrice - minPrice) / tickSize) * tickSize + minPrice

  const pricePrecision = symbolInfo.pricePrecision
  const priceStr = price.toFixed(pricePrecision)
  return priceStr
}

function capitalizeFirstLetter(str) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}


function getDefaultState() {
  return {
    balance: initialBalance,
    pnl: 0,
    feeTotal: 0,
    positionSize: 0,
    // 
    inLong: false,
    ignoreLong: 0,
    pnlLong: 0,
    balanceLong: initialBalance,
    longPosition: 0,
    longWin: 0,
    longLoss: 0,
    longWins: 0,
    longLosses: 0,
    feeLong: 0,
    entryPointsLong: [],
    actionsLong: [],
    // 
    inShort: false,
    ignoreShort: 0,
    pnlShort: 0,
    balanceShort: initialBalance,
    shortPosition: 0,
    shortWin: 0,
    shortLoss: 0,
    shortWins: 0,
    shortLosses: 0,
    feeShort: 0,
    entryPointsShort: [],
    actionsShort: [],
  }
}


function getExpectancy(opts) {
  const { side, lastWave, prevWave, optimalStats } = opts

  const configs = strategiesConfig.filter(config => config.setupWaveType === lastWave.type
    && (config.setupWaveDirection === lastWave.direction || config.setupWaveDirection === 'any')
    && (config.prevWaveType === prevWave.type || config.prevWaveType === 'any')
    && (config.side === side || config.side === 'any'));

  // eslint-disable-next-line no-loop-func
  const expectancies = configs.map(config => ({
    title: config.title,
    expectancy: calculateFadeExpectancy(optimalStats, config, {
      commission: (marketFee * 2) * (1 - cashbackRate), // 0.002,
      stopMultiplier: BacktestSettings.expectancyStopMultiplier,
      takeShift: BacktestSettings.expectancyTakeShift,
      averagingMode: BacktestSettings.expectancyMode.value
    })
  })).filter(result => result?.expectancy !== null)


  const blendedExpectancy = getBlendedExpectancy(expectancies)

  return { expectancies, blendedExpectancy }
}


function updatePerf(start, key, perfResults) {
  const duration = performance.now() - start;
  if (!perfResults[key]) {
    perfResults[key] = { total: 0, count: 0, times: [] }
  }
  perfResults[key].total += duration
  perfResults[key].count++;
  perfResults[key].times.push(duration);
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}


function calculateClusterWeights(clusters, weightingField, groupWeight = 1) {
  // 1. Проверяем корректность входных данных
  if (!Array.isArray(clusters) || clusters.length === 0) {
    // console.error("Ошибка: На вход должен быть передан непустой массив кластеров.");
    return {
      total: 0,
      weights: []
    };
  }

  if (weightingField === 'equal') {
    const numClusters = clusters.length;
    const weights = Array(numClusters).fill(groupWeight / numClusters)
    return {
      total: 1,
      weights
    };
  }

  // 2. Извлекаем "сырые" баллы из каждого кластера.
  // Если балл отрицательный или не является числом (например, поля нет), считаем его равным 0.
  const rawScores = clusters.map(cluster => {
    const score = cluster[weightingField];
    if (typeof score !== 'number' || score < 0) {
      // Кластер с отрицательным или отсутствующим показателем не должен получать вес.
      return 0;
    }
    return score;
  });

  // 3. Считаем общую сумму всех баллов.
  const totalScore = rawScores.reduce((sum, score) => sum + score, 0);

  // 4. Обрабатываем крайний случай, когда сумма баллов равна 0.
  // (например, все кластеры имеют отрицательное матожидание).
  if (totalScore === 0) {
    const numClusters = clusters.length;
    const weights = Array(numClusters).fill(groupWeight / numClusters)
    return {
      total: 1,
      weights
    };
  }

  // 5. Нормализуем каждый балл, чтобы получить итоговый вес (долю).
  const weights = rawScores.map(score => groupWeight * score / totalScore);

  return {
    total: totalScore,
    weights,
  };
}



/**
 * Рассчитывает размеры позиций для группы потенциальных сделок (кластеров).
 *
 * @param {Array<object>} potentialTrades - Массив объектов сделок.
 * @param {number} totalRiskPercent - Общий риск на весь сетап в процентах (например, 1 для 1%).
 * @param {number} balance - Текущий баланс счета.
 * @returns {Array<object>} - Массив `potentialTrades` с добавленным полем `size`.
 */
export function calculatePositionSizes(potentialTrades, totalRiskPercent, balance) {
  // 1. Расчет "оценки качества" для каждого кластера
  const tradesWithScores = potentialTrades.map(trade => {
    const expectancy = trade.cluster.netExpectancy || 0;
    const probability = trade.cluster.prob || 0;
    // const qualityScore = probability
    const qualityScore = expectancy > 0 ? probability : 0;
    // const qualityScore = expectancy > 0 ? expectancy * probability : 0;
    return { ...trade, qualityScore };
  });

  // 2. Расчет общей суммы "оценок качества"
  const totalQualityScore = tradesWithScores.reduce((sum, trade) => sum + trade.qualityScore, 0);

  if (totalQualityScore === 0) {
    return potentialTrades.map(trade => ({ ...trade, size: 0 }));
  }

  const sizedTrades = tradesWithScores.map(trade => {
    const totalRiskAmount = balance * totalRiskPercent;
    const riskShare = trade.qualityScore / totalQualityScore;

    // --- ДИАГНОСТИКА ---
    if (isNaN(totalRiskAmount) || isNaN(riskShare)) {
      console.error('Ошибка: totalRiskAmount или riskShare is NaN', { balance, totalRiskPercent, totalRiskAmount, riskShare });
      return { ...trade, size: 0 };
    }

    const totalWeight = trade.enters.reduce((sum, entry) => sum + entry.weight, 0);
    if (totalWeight === 0) return { ...trade, size: 0 };

    const avgEntryPrice = trade.enters.reduce((sum, entry) => sum + entry.price * entry.weight, 0) / totalWeight;
    const stopLossDistance = Math.abs(avgEntryPrice - trade.sl);

    // --- ДИАГНОСТИКА ---
    if (isNaN(avgEntryPrice) || isNaN(stopLossDistance)) {
      console.error('Ошибка: avgEntryPrice или stopLossDistance is NaN', { avgEntryPrice, stopLossDistance, sl: trade.sl });
      return { ...trade, size: 0 };
    }

    if (stopLossDistance <= 0) {
      console.warn('Внимание: Нулевое или отрицательное расстояние до стопа', { avgEntryPrice, sl: trade.sl });
      return { ...trade, size: 0 };
    }

    const positionSize = (totalRiskAmount * riskShare) / stopLossDistance;

    // --- ДИАГНОСТИКА ---
    if (isNaN(positionSize)) {
      console.error('ИТОГОВАЯ ОШИБКА: positionSize is NaN. Проверьте компоненты:', {
        totalRiskAmount,
        riskShare,
        stopLossDistance
      });
      return { ...trade, size: 0 };
    }

    return { ...trade, size: positionSize };
  });

  return sizedTrades;
}